import { useEffect, useState, useCallback } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { errText } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { ViewTabs } from "../components/ViewTabs";
import { Search, ChevronDown, ChevronRight, ArrowLeft, ArrowUp, ArrowDown, Loader2, ShieldCheck, RefreshCw, MessageSquare, Megaphone, Rocket, RotateCcw, Bug, Lightbulb, Plus, Send, Archive as ArchiveIcon, Pencil, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import { DateField } from "../components/DateField";

const fmtIT = (d) => { if (!d) return "—"; const [y, m, dd] = String(d).slice(0, 10).split("-"); return (y && m && dd) ? `${dd}/${m}/${y}` : String(d); };

const isOnline = (iso) => { if (!iso) return false; const t = new Date(iso).getTime(); return !isNaN(t) && (Date.now() - t) < 5 * 60000; };
const activityLabel = (iso) => {
  if (!iso) return "mai";
  const t = new Date(iso).getTime();
  if (isNaN(t)) return "—";
  const min = Math.floor((Date.now() - t) / 60000);
  if (min < 1) return "adesso";
  if (min < 60) return `${min} min fa`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h fa`;
  return `il ${fmtIT(iso)}`;
};
const PROGRAM_LABEL = { none: null, tester: "Tester", early_tester: "Early Tester", founder: "Founder" };
const LICENSE_VIEW_LABEL = { trial: ["Trial", "bg-sky-600"], active: ["Attiva", "bg-emerald-600"], beta: ["Beta attiva", "bg-[#C9A227] text-[#1A2023]"], suspended: ["Sospesa", "bg-amber-600"], expired: ["Scaduta", "bg-red-600"], missing: ["Assente", "bg-neutral-500"] };
function LicenseViewBadge({ v }) { const [l, c] = LICENSE_VIEW_LABEL[v || "missing"] || ["—", "bg-neutral-500"]; return <span className={`text-[10px] font-mono-label rounded-full px-2 py-0.5 text-white ${c}`}>{l}</span>; }
function Cell({ label, children }) { return <div className="flex items-center justify-between lg:block gap-2 min-w-0"><span className="lg:hidden text-[10px] text-[#6F8377] font-mono-label">{label}</span><span className="min-w-0 truncate inline-block max-w-full align-middle">{children}</span></div>; }
const lastActivityText = (s) => s.never_used ? "Mai utilizzato" : (s.is_inactive ? `Inattivo da ${s.inactive_days} gg` : activityLabel(s.last_activity_at));
const PROGRAM_OPTIONS = [["none", "Nessuno / Standard"], ["tester", "Tester"], ["early_tester", "Early Tester"], ["founder", "Founder"]];

const ROLE_LABEL = { admin: "Gestore", manager: "Gestore", staff: "Staff", owner: "Proprietario cavallo", readonly: "Lettura" };
const GROUPS = [
  { key: "gestori", label: "Gestori", roles: ["admin", "manager"] },
  { key: "staff", label: "Staff", roles: ["staff"] },
  { key: "owner", label: "Proprietari cavallo", roles: ["owner"] },
];

export default function PlatformAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stables, setStables] = useState([]);
  const [acctReqs, setAcctReqs] = useState([]);
  const [stableReqs, setStableReqs] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [query, setQuery] = useState("");
  const [view, setView] = useState("active");
  const [licFilter, setLicFilter] = useState("all");
  const [progFilter, setProgFilter] = useState("all");
  const [sortAct, setSortAct] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [typed, setTyped] = useState("");
  const [newFeedback, setNewFeedback] = useState(0);
  const [macroTab, setMacroTab] = useState(() => {
    try { return localStorage.getItem("pa_macro_tab") || "stables"; } catch { return "stables"; }
  });
  const setTab = (t) => { setMacroTab(t); try { localStorage.setItem("pa_macro_tab", t); } catch { /* noop */ } };

  const load = useCallback(() => {
    api.get("/admin/stables").then((r) => setStables(r.data)).catch(() => {});
    api.get("/admin/account-deletion-requests?status=all").then((r) => setAcctReqs(r.data)).catch(() => {});
    api.get("/admin/stable-admin-requests?status=all").then((r) => setStableReqs(r.data)).catch(() => {});
    api.get("/admin/licenses").then((r) => setLicenses(r.data)).catch(() => {});
    api.get("/admin/feedback?status=new").then((r) => setNewFeedback(r.data.length)).catch(() => {});
  }, []);
  useEffect(() => { if (user?.platform_admin) load(); }, [user, load]);

  const ask = (cfg) => { setTyped(""); setConfirm(cfg); };
  const openAssist = async (stableId) => {
    try { await api.post(`/admin/stables/${stableId}/assistance/enter`); } catch { /* noop */ }
    localStorage.setItem("equiro_assist", stableId);
    navigate("/");
  };

  if (user && !user.platform_admin) return <Navigate to="/" replace />;

  const q = query.trim().toLowerCase();
  const bucket = stables.filter((s) => {
    if (view === "hidden") return s.pa_hidden;
    if (s.pa_hidden) return false;   // hidden stables are excluded from every other view
    if (view === "active") return (s.status_norm || "active") === "active";
    if (view === "suspended") return (s.status_norm || "active") === "suspended";
    if (view === "never") return s.never_used;
    if (view === "inactive") return s.is_inactive;
    return true;                      // "all"
  });
  const filtered = bucket.filter((s) => {
    if (licFilter !== "all" && (s.license?.status || "missing") !== licFilter) return false;
    if (progFilter !== "all" && (s.program_status || "none") !== progFilter) return false;
    if (!q) return true;
    return ["name", "denominazione", "email", "asl_code", "gestore_name", "gestore_email"]
      .some((k) => (s[k] || "").toLowerCase().includes(q));
  });
  const sorted = [...filtered].sort((a, b) => {
    const av = a.last_activity_at || "", bv = b.last_activity_at || "";
    const c = av < bv ? -1 : av > bv ? 1 : 0;
    return sortAct ? c : -c;   // default: most recent first
  });
  const available = licenses.filter((l) => !l.stable_id);

  const doDismiss = async () => {
    try { await confirm.run(typed); toast.success(confirm.okMsg || "Operazione eseguita"); setConfirm(null); setTyped(""); load(); }
    catch (err) { toast.error(errText(err.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-[#1A2023] text-[#EAE4D3]">
      <header className="border-b border-[#333B3C] bg-[#1A2023]/95 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-6 w-6 text-[#C9A227]" />
            <div>
              <p className="font-mono-label text-[10px] text-[#C9A227]">EQUIRO</p>
              <h1 className="font-display text-xl font-semibold flex items-center gap-2">Console Platform Admin
                {newFeedback > 0 && (
                  <button type="button" data-testid="admin-new-feedback-badge"
                    onClick={() => { setTab("product"); setTimeout(() => document.getElementById("admin-feedback-section")?.scrollIntoView({ behavior: "smooth" }), 80); }}
                    className="inline-flex items-center gap-1 rounded-full bg-[#C9A227] text-[#1A2023] text-[10px] font-mono-label px-2 py-0.5">
                    <MessageSquare className="h-3 w-3" /> {newFeedback} {newFeedback === 1 ? "nuovo feedback" : "nuovi feedback"}
                  </button>
                )}
              </h1>
            </div>
          </div>
          <Button data-testid="back-to-stable-btn" onClick={() => navigate("/")}
            className="bg-[#2E6B4B] hover:bg-[#347D57] text-[#EAE4D3]">
            <ArrowLeft className="h-4 w-4 mr-2" /> Torna alla scuderia
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <div className="overflow-x-auto -mx-1 px-1">
          <ViewTabs
            className="whitespace-nowrap"
            value={macroTab}
            onChange={setTab}
            options={[
              { value: "stables", label: "Scuderie & Licenze", testid: "pa-tab-stables" },
              { value: "product", label: "Prodotto & Feedback", testid: "pa-tab-product" },
              { value: "config", label: "Dizionari & Configurazioni", testid: "pa-tab-config" },
              { value: "legal", label: "Legal & Privacy", testid: "pa-tab-legal" },
            ]}
          />
        </div>

        {macroTab === "legal" && (
          <div className="py-2" data-testid="pa-legal-block">
            <p className="text-sm text-[#5F6B64] mb-3">Gestione documenti legali, versioni, accettazioni e registro servizi.</p>
            <a href="/legal-console" className="inline-flex items-center rounded-lg bg-[#1B3B2B] hover:bg-[#142E1F] text-white text-sm font-medium px-4 py-2" data-testid="pa-open-legal-console">Apri Console Legal &amp; Privacy</a>
          </div>
        )}

        {macroTab === "stables" && (
        <div className="space-y-8">
        <section data-testid="admin-stables-section">
          <TesterInvitesSection onRollbackDone={load} />
          <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
            <h2 className="font-display text-lg font-semibold">Scuderie <span className="text-[#8FA396]">({sorted.length})</span></h2>
            <div className="relative w-full sm:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#C9A227]" />
              <input data-testid="admin-stable-search"
                className="w-full pl-9 pr-3 py-2 rounded-md bg-[#232A2C] border border-[#333B3C] text-sm outline-none placeholder:text-[#6F8377]"
                placeholder="Cerca scuderia, gestore o email…"
                value={query} onChange={(e) => setQuery(e.target.value)} />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="admin-stable-filters">
            <div className="inline-flex flex-wrap rounded-md border border-[#333B3C] bg-[#232A2C] p-0.5" data-testid="admin-stable-views">
              {[["active", "Attivi"], ["inactive", "Inattivi / Da verificare"], ["never", "Mai usato"], ["hidden", "Nascosti"], ["suspended", "Sospesi"], ["all", "Tutti"]].map(([v, l]) => (
                <button key={v} data-testid={`view-${v}`} onClick={() => setView(v)} className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${view === v ? "bg-[#2E6B4B] text-[#EAE4D3]" : "text-[#8FA396]"}`}>{l}</button>
              ))}
            </div>
            <button data-testid="sort-activity" onClick={() => setSortAct((x) => !x)} className="rounded-md bg-[#232A2C] border border-[#333B3C] text-xs px-2 py-1.5 text-[#EAE4D3] inline-flex items-center gap-1">Ultima attività {sortAct ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}</button>
            <select data-testid="filter-license" value={licFilter} onChange={(e) => setLicFilter(e.target.value)} className="rounded-md bg-[#232A2C] border border-[#333B3C] text-xs px-2 py-1.5 text-[#EAE4D3]">
              <option value="all">Licenza: tutte</option><option value="trial">Trial</option><option value="active">Attiva</option><option value="expired">Scaduta</option><option value="suspended">Sospesa</option><option value="missing">Assente</option>
            </select>
            <select data-testid="filter-program" value={progFilter} onChange={(e) => setProgFilter(e.target.value)} className="rounded-md bg-[#232A2C] border border-[#333B3C] text-xs px-2 py-1.5 text-[#EAE4D3]">
              <option value="all">Programma: tutti</option><option value="none">Standard</option><option value="tester">Tester</option><option value="early_tester">Early Tester</option><option value="founder">Founder</option>
            </select>
          </div>
          <div className="hidden lg:grid grid-cols-[minmax(0,2.2fr)_repeat(7,minmax(0,1fr))] gap-3 px-4 pb-1 text-[10px] font-mono-label text-[#6F8377]" data-testid="pa-stables-header">
            <span>Scuderia</span><span>Stato</span><span>Licenza</span><span>Programma</span><span>Ultima attività</span><span>Gestori</span><span>Staff</span><span>Proprietari</span>
          </div>
          <div className="space-y-2">
            {sorted.map((s) => <StableRow key={s.id} s={s} ask={ask} reload={load} onAssist={openAssist} />)}
            {sorted.length === 0 && <p className="text-sm text-[#8FA396]">Nessuna scuderia trovata.</p>}
          </div>
        </section>

        <UsersSection />

        <DuplicateStablesSection />

        <GlobalHorseSearchSection onAssist={openAssist} />

        <section data-testid="admin-licenses-section">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h2 className="font-display text-lg font-semibold">Licenze disponibili <span className="text-[#8FA396]">({available.length})</span></h2>
          </div>
          <div className="space-y-2 mt-3">
            {available.map((l) => (
              <div key={l.id} data-testid={`avail-license-${l.id}`}
                className="rounded-lg border border-[#333B3C] bg-[#232A2C] px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{l.assigned_to_email || "—"} <LicenseBadge status={l.effective_status} /></p>
                  <p className="text-xs text-[#8FA396] truncate">
                    Non ancora usata · creata {fmtIT(l.created_at)}
                    {l.expires_at ? ` · scade ${fmtIT(l.expires_at)}` : ""}{l.notes ? ` · ${l.notes}` : ""}
                  </p>
                </div>
                <Button size="sm" variant="outline" data-testid={`revoke-license-${l.id}`}
                  onClick={() => ask({ title: "Revocare la licenza disponibile?", body: `La licenza per ${l.assigned_to_email || "—"} verrà eliminata definitivamente. È consentito solo perché non è ancora associata a una scuderia.`, okMsg: "Licenza revocata", run: () => api.delete(`/admin/licenses/${l.id}`) })}
                  className="border-red-500 text-red-300 hover:bg-red-500/10 shrink-0">Revoca</Button>
              </div>
            ))}
            {available.length === 0 && <p className="text-sm text-[#8FA396]">Nessuna licenza disponibile. Le licenze nascono con la scuderia; per casi eccezionali usa "Assegna licenza manuale a una scuderia" nelle Azioni amministrative avanzate.</p>}
          </div>
        </section>
        </div>
        )}

        {macroTab === "config" && (
        <div className="space-y-8">
          <DictionariesSection />
        </div>
        )}

        {macroTab === "product" && (
        <div className="space-y-8">
          <FeedbackAdminSection />
          <RoadmapAdminSection />
          <ReleaseNotesAdminSection />
        </div>
        )}

        {macroTab === "stables" && (
        <CollapsibleSection title="Azioni amministrative avanzate (licenze manuali, eliminazioni & richieste)" testid="admin-advanced" defaultOpen={false} danger>
        <section data-testid="admin-manual-license" className="mb-6">
          <h3 className="font-display font-semibold text-[#EAE4D3] mb-1">Assegna licenza manuale a una scuderia</h3>
          <p className="text-xs text-[#8FA396] mb-3">Azione eccezionale (supporto, onboarding assistito, demo). Le licenze nascono normalmente con la scuderia; qui puoi pre-assegnarne una a un Gestore per aprire una nuova scuderia.</p>
          <AvailableLicenseForm onCreated={load} />
          <TrialDaysControl />
        </section>
        <section data-testid="admin-requests-section" className="grid lg:grid-cols-2 gap-6">
          <RequestCard title="Richieste eliminazione account" testid="acct-requests" empty="Nessuna richiesta.">
            {acctReqs.map((r) => (
              <div key={r.id} className="flex items-center justify-between py-2.5 gap-2" data-testid={`acct-req-${r.id}`}>
                <div>
                  <p className="text-sm font-medium">{r.user_name} <ReqStatus s={r.status} /></p>
                  <p className="text-xs text-[#8FA396]">{r.user_email} · {fmtIT(r.created_at)}</p>
                </div>
                {r.status === "pending" && (
                  <div className="flex gap-1.5 shrink-0">
                    <Button size="sm" data-testid={`execute-acct-${r.id}`}
                      onClick={() => ask({ title: "Eliminare definitivamente l'account?", body: `Account, credenziali e membership di ${r.user_name} verranno eliminati. Lo storico della scuderia è conservato (autore anonimizzato). Bloccato se è l'unico Gestore di una scuderia attiva.`, okMsg: "Account eliminato", run: () => api.post(`/admin/account-deletion-requests/${r.id}/execute`) })}
                      className="bg-red-700 hover:bg-red-800 text-white">Esegui</Button>
                    <Button size="sm" variant="outline" data-testid={`dismiss-acct-${r.id}`}
                      onClick={() => ask({ title: "Chiudere la richiesta?", body: `La richiesta di ${r.user_name} verrà chiusa. Nessun account viene eliminato.`, okMsg: "Richiesta chiusa", run: () => api.post(`/admin/account-deletion-requests/${r.id}/dismiss`) })}
                      className="border-[#333B3C] text-[#EAE4D3] hover:bg-[#2E6B4B]">Chiudi</Button>
                  </div>
                )}
              </div>
            ))}
          </RequestCard>
          <RequestCard title="Richieste eliminazione/disattivazione scuderia" testid="stable-requests" empty="Nessuna richiesta.">
            {stableReqs.map((r) => (
              <div key={r.id} className="flex items-center justify-between py-2.5 gap-2" data-testid={`stable-req-${r.id}`}>
                <div>
                  <p className="text-sm font-medium">{r.stable_name} <ReqStatus s={r.status} /></p>
                  <p className="text-xs text-[#8FA396]">{r.type === "delete" ? "Eliminazione" : "Disattivazione"} · {fmtIT(r.created_at)}</p>
                </div>
                {r.status === "pending" && (
                  <div className="flex gap-1.5 shrink-0">
                    <Button size="sm" data-testid={`execute-stable-${r.id}`}
                      onClick={() => ask({ title: r.type === "delete" ? "Eliminare la scuderia?" : "Disattivare la scuderia?", body: r.type === "delete" ? "Digita il nome esatto per confermare. Soft-delete: i dati storici vengono CONSERVATI." : `La scuderia "${r.stable_name}" verrà disattivata. I dati sono conservati.`, needName: r.type === "delete", expectName: r.stable_name, okMsg: "Richiesta eseguita", run: (name) => api.post(`/admin/stable-admin-requests/${r.id}/execute`, { confirm_name: name }) })}
                      className="bg-red-700 hover:bg-red-800 text-white">Esegui</Button>
                    <Button size="sm" variant="outline" data-testid={`dismiss-stable-${r.id}`}
                      onClick={() => ask({ title: "Chiudere la richiesta?", body: `La richiesta per "${r.stable_name}" verrà chiusa. Nessun dato viene modificato.`, okMsg: "Richiesta chiusa", run: () => api.post(`/admin/stable-admin-requests/${r.id}/dismiss`) })}
                      className="border-[#333B3C] text-[#EAE4D3] hover:bg-[#2E6B4B]">Chiudi</Button>
                  </div>
                )}
              </div>
            ))}
          </RequestCard>
        </section>

        <p className="text-xs text-[#6F8377] max-w-3xl">
          Nota: le azioni "Esegui" applicano modifiche definitive. L'eliminazione account rimuove
          account, credenziali e membership (i record storici della scuderia sono conservati e privi di
          riferimento personale). Sospensione, disattivazione ed eliminazione scuderia sono soft: i dati
          storici vengono sempre conservati. "Chiudi" archivia la richiesta senza modificare alcun dato.
        </p>
        </CollapsibleSection>
        )}
      </main>

      {confirm && (
        <Dialog open={!!confirm} onOpenChange={(o) => { if (!o) { setConfirm(null); setTyped(""); } }}>
          <DialogContent data-testid="admin-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="font-display">{confirm.title}</DialogTitle>
              <DialogDescription>{confirm.body}</DialogDescription>
            </DialogHeader>
            {confirm.needName && (
              <Input data-testid="admin-confirm-name" value={typed}
                onChange={(e) => setTyped(e.target.value)} placeholder={confirm.expectName}
                className="bg-[#232A2C] border-[#333B3C] text-[#EAE4D3]" />
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setConfirm(null); setTyped(""); }} data-testid="admin-confirm-cancel">Annulla</Button>
              <Button onClick={doDismiss} data-testid="admin-confirm-ok"
                disabled={confirm.needName && typed.trim() !== (confirm.expectName || "").trim()}
                className="bg-red-700 hover:bg-red-800 text-white">Conferma</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function PermanentDeleteDialog({ s, onClose, onDone }) {
  const [preview, setPreview] = useState(undefined);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/admin/stables/${s.id}/permanent-delete-preview`).then((r) => setPreview(r.data)).catch(() => setPreview(null));
  }, [s.id]);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/stables/${s.id}/permanent-delete`, { confirm_name: typed });
      toast.success(`Scuderia eliminata definitivamente · utenti eliminati ${data.users_deleted ?? 0}, preservati ${data.users_preserved ?? 0}`);
      onDone();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const blocked = preview?.blockers?.length > 0;
  const totalDocs = preview?.counts ? Object.values(preview.counts).reduce((a, b) => a + b, 0) : 0;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="perm-delete-dialog" className="bg-[#232A2C] border-red-700/60 text-[#EAE4D3] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-red-300">Elimina definitivamente</DialogTitle>
          <DialogDescription className="text-[#8FA396] text-xs">Questa operazione eliminerà definitivamente la scuderia «{s.name}» e i dati associati. Non può essere annullata.</DialogDescription>
        </DialogHeader>
        {preview === undefined ? <Loader2 className="h-5 w-5 animate-spin text-red-300 my-4" /> : preview === null ? (
          <p className="text-sm text-red-300">Impossibile caricare l'anteprima.</p>
        ) : !preview.exists ? (
          <p className="text-sm text-[#8FA396]">Scuderia già eliminata.</p>
        ) : (
          <div className="space-y-3 text-sm" data-testid="perm-delete-preview">
            <div>
              <p className="font-mono-label text-[10px] text-red-300 mb-1">Eliminabili ({totalDocs} documenti + scuderia)</p>
              <ul className="text-[#EAE4D3] grid grid-cols-2 gap-x-4 text-xs">
                {Object.entries(preview.counts || {}).map(([k, v]) => <li key={k}>{v} {k}</li>)}
                <li className="font-semibold">1 scuderia</li>
              </ul>
              {preview.users_to_delete?.length > 0 && (
                <div className="mt-2" data-testid="perm-users-delete">
                  <p className="text-[11px] text-red-300">Utenti esclusivi da eliminare:</p>
                  {preview.users_to_delete.map((u) => <p key={u.id} className="text-[11px] text-[#EAE4D3]">· {u.name} ({u.email})</p>)}
                </div>
              )}
            </div>
            {preview.users_preserved?.length > 0 && (
              <div className="rounded-md bg-sky-900/30 border border-sky-500/40 p-2" data-testid="perm-users-preserved">
                <p className="text-sky-200 text-xs font-semibold">Preservati (condivisi / Platform Admin):</p>
                {preview.users_preserved.map((u) => <p key={u.id} className="text-[11px] text-sky-100">· {u.name} ({u.email}) — {u.reason}</p>)}
              </div>
            )}
            {preview.invites?.length > 0 && (
              <p className="text-[11px] text-[#C9A227]" data-testid="perm-invites">{preview.invites.length} invito/i tester collegati → resi terminali (token non riutilizzabile).</p>
            )}
            {blocked && (
              <div className="rounded-md bg-red-900/30 border border-red-500/40 p-2" data-testid="perm-blockers">
                <p className="text-red-300 text-xs font-semibold">Bloccanti:</p>
                {preview.blockers.map((b, i) => <p key={i} className="text-[11px] text-red-200">· {typeof b === "string" ? b : JSON.stringify(b)}</p>)}
              </div>
            )}
            <div>
              <label className="text-xs text-[#8FA396]">Digita il nome esatto della scuderia per confermare:</label>
              <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={s.name} autoFocus={false}
                data-testid="perm-confirm-name" className="mt-1 bg-[#1A2023] border-[#333B3C] text-[#EAE4D3]" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="perm-cancel">Annulla</Button>
          <Button onClick={run} data-testid="perm-confirm"
            disabled={busy || blocked || !preview?.exists || typed.trim() !== s.name.trim()}
            className="bg-red-600 hover:bg-red-700 text-white">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Elimina definitivamente</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UsersSection() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [del, setDel] = useState(null);
  const load = useCallback(() => { api.get(`/admin/users`).then((r) => setUsers(r.data)).catch(() => setUsers([])); }, []);
  useEffect(() => { load(); }, [load]);
  const ql = q.toLowerCase();
  const list = users.filter((u) => !ql || (u.name || "").toLowerCase().includes(ql) || (u.email || "").toLowerCase().includes(ql));
  return (
    <section data-testid="admin-users-section" className="mt-10">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <h2 className="font-display text-lg font-semibold">Utenti <span className="text-[#8FA396]">({list.length})</span></h2>
        <div className="relative w-full sm:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#C9A227]" />
          <input data-testid="admin-user-search" className="w-full pl-9 pr-3 py-2 rounded-md bg-[#232A2C] border border-[#333B3C] text-sm outline-none placeholder:text-[#6F8377]"
            placeholder="Cerca utente per nome o email…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>
      <div className="space-y-2">
        {list.map((u) => (
          <div key={u.id} data-testid={`user-row-${u.id}`} className="rounded-xl border border-[#333B3C] bg-[#232A2C] px-4 py-2.5 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-[#EAE4D3] truncate">{u.name} <span className="text-[#8FA396]">· {u.email}</span>
                {u.platform_admin && <span className="ml-1 text-[10px] font-mono-label rounded-full px-2 py-0.5 bg-[#C9A227] text-[#1A2023]">Platform Admin</span>}</p>
              <p className="text-[11px] text-[#8FA396] truncate">{u.membership_count} scuderie{u.stables.length ? ": " + u.stables.map((s) => `${s.name} (${s.role})`).join(", ") : ""}</p>
            </div>
            {!u.platform_admin && (
              <Button size="sm" variant="outline" data-testid={`user-delete-${u.id}`} onClick={() => setDel(u)} className="border-red-600 text-red-400 hover:bg-red-600/10 shrink-0">Elimina definitivamente</Button>
            )}
          </div>
        ))}
        {list.length === 0 && <p className="text-sm text-[#8FA396]">Nessun utente trovato.</p>}
      </div>
      {del && <UserDeleteDialog u={del} onClose={() => setDel(null)} onDone={() => { setDel(null); load(); }} />}
    </section>
  );
}

function UserDeleteDialog({ u, onClose, onDone }) {
  const [preview, setPreview] = useState(undefined);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/admin/users/${u.id}/permanent-delete-preview`).then((r) => setPreview(r.data)).catch(() => setPreview(null)); }, [u.id]);
  const blocked = preview?.blockers?.length > 0;
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/permanent-delete`, { confirm_email: typed });
      toast.success(`Utente eliminato · membership rimosse ${data.removed_memberships ?? 0}`); onDone();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="user-delete-dialog" className="bg-[#232A2C] border-red-700/60 text-[#EAE4D3] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-red-300">Elimina definitivamente utente</DialogTitle>
          <DialogDescription className="text-[#8FA396] text-xs">Questa operazione eliminerà definitivamente l'utente «{u.email}» da Equiro e non può essere annullata.</DialogDescription>
        </DialogHeader>
        {preview === undefined ? <Loader2 className="h-5 w-5 animate-spin text-red-300 my-4" /> : preview === null ? (
          <p className="text-sm text-red-300">Impossibile caricare l'anteprima.</p>
        ) : !preview.exists ? <p className="text-sm text-[#8FA396]">Utente già eliminato.</p> : (
          <div className="space-y-3 text-sm" data-testid="user-delete-preview">
            {blocked && (
              <div className="rounded-md bg-red-900/30 border border-red-500/40 p-2" data-testid="user-blockers">
                <p className="text-red-300 text-xs font-semibold">Bloccanti:</p>
                {preview.blockers.map((b, i) => <p key={i} className="text-[11px] text-red-200">· {b.reason}</p>)}
              </div>
            )}
            <div>
              <p className="font-mono-label text-[10px] text-red-300 mb-1">Eliminati</p>
              <ul className="text-[11px] text-[#EAE4D3]">
                <li>Account + credenziali/token</li>
                <li>{preview.memberships.length} membership: {preview.memberships.map((m) => `${m.name} (${m.role})`).join(", ") || "—"}</li>
                {preview.invitations_by_email > 0 && <li>{preview.invitations_by_email} inviti collegati all'email</li>}
              </ul>
            </div>
            <div className="rounded-md bg-sky-900/30 border border-sky-500/40 p-2" data-testid="user-preserved">
              <p className="text-sky-200 text-xs font-semibold">Preservati</p>
              <p className="text-[11px] text-sky-100">{preview.preserved_note}{preview.instructor_links ? ` (${preview.instructor_links} profili istruttore scollegati)` : ""}{preview.license_links ? ` · ${preview.license_links} licenze scollegate` : ""}</p>
            </div>
            {!blocked && (
              <div>
                <label className="text-xs text-[#8FA396]">Digita l'email esatta dell'utente per confermare:</label>
                <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={u.email} autoFocus={false}
                  data-testid="user-confirm-email" className="mt-1 bg-[#1A2023] border-[#333B3C] text-[#EAE4D3]" />
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="user-del-cancel">Annulla</Button>
          <Button onClick={run} data-testid="user-del-confirm"
            disabled={busy || blocked || !preview?.exists || (typed.trim().toLowerCase() !== (u.email || "").trim().toLowerCase())}
            className="bg-red-600 hover:bg-red-700 text-white">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Elimina definitivamente</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StableRow({ s, ask, reload, onAssist }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [permDel, setPermDel] = useState(null);

  const refetch = async () => {
    try { const { data } = await api.get(`/admin/stables/${s.id}`); setDetail(data); }
    catch { /* noop */ }
  };
  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !detail) { setLoading(true); await refetch(); setLoading(false); }
  };

  return (
    <div className="rounded-lg border border-[#333B3C] bg-[#232A2C] overflow-hidden" data-testid={`admin-stable-${s.id}`}>
      <button onClick={toggle} data-testid={`admin-stable-toggle-${s.id}`}
        className="w-full px-4 py-3 hover:bg-[#2A3234] transition-colors text-left">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2.2fr)_repeat(7,minmax(0,1fr))] gap-2 lg:gap-3 lg:items-center">
          <div className="flex items-start gap-2 min-w-0">
            <span className="mt-0.5 shrink-0">{open ? <ChevronDown className="h-4 w-4 text-[#C9A227]" /> : <ChevronRight className="h-4 w-4 text-[#C9A227]" />}</span>
            <div className="min-w-0">
              <p className="font-semibold text-sm truncate flex items-center gap-1.5">{s.name}
                {s.pa_hidden && <span data-testid={`pa-hidden-badge-${s.id}`} className="shrink-0 text-[9px] font-mono-label rounded-full px-1.5 py-0.5 bg-[#333B3C] text-[#8FA396]">Nascosta</span>}</p>
              <p className="text-[11px] text-[#8FA396] truncate">{s.denominazione || "—"}{s.asl_code ? ` · Cod. ${s.asl_code}` : ""}{s.comune ? ` · ${s.comune}` : ""}</p>
            </div>
          </div>
          <Cell label="Stato"><StatusBadge s={s.status_norm || s.status} /></Cell>
          <Cell label="Licenza"><LicenseViewBadge v={s.license_view} /></Cell>
          <Cell label="Programma">{PROGRAM_LABEL[s.program_status] ? <span data-testid={`pa-program-${s.id}`} className="text-[10px] font-mono-label rounded-full px-2 py-0.5 bg-[#3A2E12] text-[#C9A227] border border-[#C9A227]/40">{PROGRAM_LABEL[s.program_status]}</span> : <span className="text-[11px] text-[#6F8377]">—</span>}</Cell>
          <Cell label="Ultima attività"><span data-testid={`pa-activity-${s.id}`} className={`text-[11px] inline-flex items-center gap-1 ${s.never_used ? "text-[#8FA396]" : s.is_inactive ? "text-amber-300" : "text-[#EAE4D3]"}`}>{isOnline(s.last_activity_at) && <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" />}{lastActivityText(s)}</span></Cell>
          <Cell label="Gestori"><span className="text-sm text-[#EAE4D3]">{s.gestori}</span></Cell>
          <Cell label="Staff"><span className="text-sm text-[#EAE4D3]">{s.staff}</span></Cell>
          <Cell label="Proprietari"><span className="text-sm text-[#EAE4D3]">{s.owner}</span></Cell>
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-[#333B3C]" data-testid={`admin-stable-detail-${s.id}`}>
          {loading && <Loader2 className="h-5 w-5 animate-spin text-[#C9A227] my-3" />}
          {detail && (
            <div className="space-y-4 pt-3">
              <div className="flex flex-wrap gap-2" data-testid={`admin-actions-${s.id}`}>
                <Button size="sm" variant="outline" data-testid={`assist-${s.id}`} onClick={() => onAssist(s.id)}
                  className="border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10">Apri in assistenza</Button>
                {!s.pa_hidden ? (
                  <Button size="sm" variant="outline" data-testid={`hide-${s.id}`}
                    onClick={() => ask({ title: "Nascondere dalla vista?", body: "Solo preferenza organizzativa Platform Admin. NON sospende, non disabilita account, non modifica licenza né dati. Recuperabile dalla vista 'Nascosti'.", okMsg: "Scuderia nascosta", run: () => api.post(`/admin/stables/${s.id}/hide`) })}
                    className="border-neutral-400 text-neutral-200 hover:bg-neutral-500/10">Nascondi dalla vista</Button>
                ) : (
                  <Button size="sm" variant="outline" data-testid={`unhide-${s.id}`}
                    onClick={() => ask({ title: "Mostrare di nuovo?", body: "La scuderia tornerà nelle viste principali. Nessun altro effetto.", okMsg: "Scuderia ripristinata", run: () => api.post(`/admin/stables/${s.id}/unhide`) })}
                    className="border-emerald-500 text-emerald-300 hover:bg-emerald-500/10">Mostra di nuovo</Button>
                )}
                <Button size="sm" variant="outline" data-testid={`tester-welcome-${s.id}`}
                  onClick={() => ask({ title: "Inviare email di benvenuto tester?", body: "Verrà inviata al Gestore l'email di benvenuto tester (accesso gratuito durante la Beta). Nessuna modifica a licenza o stato.", okMsg: "Email inviata", run: () => api.post(`/admin/stables/${s.id}/tester-welcome`) })}
                  className="border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10">Email benvenuto tester</Button>
                {s.license && s.license.license_view !== "beta" && (
                  <Button size="sm" variant="outline" data-testid={`admit-beta-${s.id}`}
                    onClick={() => ask({ title: "Ammettere alla Beta?", body: "La licenza diventa 'Beta attiva' (operativa, senza scadenza) e viene inviata l'email trial→Beta al Gestore. Nessun prezzo, nessuna scadenza Beta, nessuna conversione automatica a pagamento.", okMsg: "Ammessa alla Beta", run: () => api.post(`/admin/stables/${s.id}/admit-beta`) })}
                    className="border-sky-500 text-sky-300 hover:bg-sky-500/10">Ammetti alla Beta</Button>
                )}
                {(s.status_norm || s.status) !== "suspended" && (
                  <Button size="sm" variant="outline" data-testid={`suspend-${s.id}`}
                    onClick={() => ask({ title: "Sospendere la scuderia?", body: "Accesso operativo temporaneamente sospeso. La scuderia resta esistente e i dati sono conservati. Reversibile.", okMsg: "Scuderia sospesa", run: () => api.post(`/admin/stables/${s.id}/status`, { status: "suspended" }) })}
                    className="border-amber-500 text-amber-300 hover:bg-amber-500/10">Sospendi</Button>
                )}
                {(s.status_norm || s.status) !== "archived" && (
                  <Button size="sm" variant="outline" data-testid={`archive-${s.id}`}
                    onClick={() => ask({ title: "Archiviare la scuderia?", body: "Non più operativa ma conservata per lo storico. Cavalli, utenti e storico NON vengono eliminati. Reversibile.", okMsg: "Scuderia archiviata", run: () => api.post(`/admin/stables/${s.id}/status`, { status: "archived" }) })}
                    className="border-neutral-400 text-neutral-200 hover:bg-neutral-500/10">Archivia</Button>
                )}
                {(s.status_norm || s.status) !== "active" && (
                  <Button size="sm" variant="outline" data-testid={`reactivate-${s.id}`}
                    onClick={() => ask({ title: "Riattivare la scuderia?", body: "La scuderia tornerà pienamente operativa (status attivo).", okMsg: "Scuderia riattivata", run: () => api.post(`/admin/stables/${s.id}/status`, { status: "active" }) })}
                    className="border-emerald-500 text-emerald-300 hover:bg-emerald-500/10">Riattiva</Button>
                )}
                <Button size="sm" variant="outline" data-testid={`perm-delete-${s.id}`}
                  onClick={() => setPermDel(s)}
                  className="border-red-600 text-red-400 hover:bg-red-600/10">Elimina definitivamente</Button>
              </div>
              <div>
                <p className="font-mono-label text-[10px] text-[#C9A227] mb-1">Anagrafica</p>
                <div className="grid sm:grid-cols-3 gap-x-6 gap-y-1 text-sm">
                  <Field label="ID Equiro" value={detail.stable.id} mono />
                  <Field label="Codice stalla" value={detail.stable.asl_code} />
                  <Field label="Denominazione" value={detail.stable.denominazione} />
                  <Field label="P. IVA" value={detail.stable.partita_iva} />
                  <Field label="Cod. fiscale" value={detail.stable.codice_fiscale} />
                  <Field label="Comune" value={detail.stable.comune} />
                  <Field label="Provincia" value={detail.stable.provincia} />
                  <Field label="Telefono" value={detail.stable.telefono} />
                  <Field label="Email" value={detail.stable.email} />
                  <Field label="PEC" value={detail.stable.pec} />
                  <Field label="Referente" value={detail.stable.referente} />
                </div>
              </div>
              <LicensePanel stableId={s.id} license={detail.license}
                onChanged={() => { refetch(); reload && reload(); }} />
              <ProgramControl stableId={s.id} value={detail.stable.program_status} since={detail.stable.program_since} note={detail.stable.program_note}
                onChanged={() => { refetch(); reload && reload(); }} />
              {GROUPS.map((g) => {
                const items = detail.members.filter((m) => g.roles.includes(m.role));
                if (items.length === 0) return null;
                return (
                  <div key={g.key} data-testid={`admin-group-${s.id}-${g.key}`}>
                    <p className="font-mono-label text-[10px] text-[#C9A227] mb-1">{g.label} ({items.length})</p>
                    <div className="space-y-1">
                      {items.map((m) => (
                        <div key={m.user_id} className="text-sm flex flex-wrap items-center gap-x-2" data-testid={`admin-member-${m.user_id}`}>
                          <span className="font-medium">{m.name}</span>
                          <span className="text-[#8FA396] text-xs">{m.email}</span>
                          <span className="font-mono-label text-[9px] rounded-full px-1.5 py-0.5 bg-[#2E6B4B] text-[#C9A227]">{ROLE_LABEL[m.role]}</span>
                          <span className="text-[10px] text-[#8FA396] inline-flex items-center gap-1" data-testid={`admin-member-activity-${m.user_id}`}>{isOnline(m.last_activity_at) && <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />}{isOnline(m.last_activity_at) ? "Online" : `Attivo ${activityLabel(m.last_activity_at)}`}</span>
                          {m.role === "owner" && m.owned_horses?.length > 0 && (
                            <span className="text-xs text-[#8FA396]">→ {m.owned_horses.map((h) => h.display_name).join(", ")}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {permDel && <PermanentDeleteDialog s={permDel} onClose={() => setPermDel(null)} onDone={() => { setPermDel(null); reload(); }} />}
    </div>
  );
}

function ProgramControl({ stableId, value, since, note, onChanged }) {
  const [prog, setProg] = useState(value || "none");
  const [pnote, setPnote] = useState(note || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.post(`/admin/stables/${stableId}/program`, { program_status: prog, program_note: pnote || null }); toast.success("Programma aggiornato"); onChanged && onChanged(); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <div data-testid={`program-control-${stableId}`}>
      <p className="font-mono-label text-[10px] text-[#C9A227] mb-1">Programma (informativo · non modifica ruolo/licenza)</p>
      <div className="flex flex-wrap items-center gap-2">
        <select data-testid={`program-select-${stableId}`} value={prog} onChange={(e) => setProg(e.target.value)}
          className="rounded-md bg-[#232A2C] border border-[#333B3C] text-sm px-2 py-1.5 text-[#EAE4D3]">
          {PROGRAM_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input data-testid={`program-note-${stableId}`} value={pnote} onChange={(e) => setPnote(e.target.value)} placeholder="Nota (opz.)"
          className="flex-1 min-w-[160px] rounded-md bg-[#232A2C] border border-[#333B3C] text-sm px-2 py-1.5 text-[#EAE4D3] placeholder:text-[#6F8377]" />
        <Button size="sm" data-testid={`program-save-${stableId}`} onClick={save} disabled={saving} className="bg-[#2E6B4B] hover:bg-[#347D57]">{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Salva</Button>
      </div>
      {since && prog !== "none" && <p className="text-[11px] text-[#8FA396] mt-1">Dal {fmtIT(since)}</p>}
    </div>
  );
}

const INVITE_STATUS = { available: ["Disponibile", "bg-emerald-600"], used: ["Usato", "bg-sky-600"], revoked: ["Revocato", "bg-neutral-500"], expired: ["Scaduto", "bg-amber-600"], onboarding_deleted: ["Onboarding eliminato", "bg-red-700"], onboarding_failed: ["Fallito", "bg-red-600"] };
const PKG_STATUS = { prepared: "Preparato", available: "Pronto per import", in_progress: "Import in corso", completed: "Import completato", cancelled: "Annullato" };

function LegacyAttachDialog({ inviteId, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const analyze = async () => {
    if (!file) { toast.error("Seleziona il file CSV"); return; }
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/legacy-import/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setParsed(data);
    } catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const prepare = async (legacyStableId) => {
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); fd.append("invite_id", inviteId); fd.append("legacy_stable_id", legacyStableId);
      await api.post("/admin/legacy-import/prepare", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Pacchetto import associato"); onDone();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const stables = (parsed?.stables || []).filter((s) => s.legacy_stable_id.toLowerCase().includes(q.toLowerCase()));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="legacy-attach-dialog" className="bg-[#232A2C] border-[#333B3C] text-[#EAE4D3] max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Associa import legacy</DialogTitle><DialogDescription className="text-[#8FA396] text-xs">Carica il CSV legacy, scegli UNA scuderia: solo i suoi cavalli verranno inclusi nel pacchetto.</DialogDescription></DialogHeader>
        {!parsed ? (
          <div className="space-y-3">
            <input type="file" accept=".csv,text/csv" data-testid="legacy-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm text-[#EAE4D3]" />
            <Button onClick={analyze} disabled={busy || !file} data-testid="legacy-analyze-btn" className="bg-[#C9A227] text-[#1A2023] hover:bg-[#b8931f]">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Analizza CSV</Button>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-[#8FA396]">{parsed.total_rows} righe · {parsed.stables.length} scuderie{parsed.blank_stable_rows ? ` · ${parsed.blank_stable_rows} riga senza scuderia (ignorata)` : ""}</p>
            <input placeholder="Cerca scuderia…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="legacy-stable-search" className="w-full rounded-md bg-[#1A2023] border border-[#333B3C] text-sm px-2 py-1.5 text-[#EAE4D3]" />
            <div className="space-y-1.5 max-h-[45vh] overflow-y-auto">
              {stables.map((s) => (
                <div key={s.legacy_stable_id} data-testid={`legacy-stable-${s.legacy_stable_id}`} className="flex items-center justify-between gap-2 rounded-md border border-[#333B3C] bg-[#1A2023] px-3 py-2">
                  <span className="text-sm text-[#EAE4D3] truncate">{s.legacy_stable_id} <span className="text-[#8FA396]">— {s.count} cavalli</span></span>
                  <Button size="sm" disabled={busy} data-testid={`legacy-prepare-${s.legacy_stable_id}`} onClick={() => prepare(s.legacy_stable_id)} className="bg-[#2E6B4B] hover:bg-[#347D57] shrink-0">Prepara</Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LegacyPackageInfo({ inviteId, refreshKey, onChanged }) {
  const [pkg, setPkg] = useState(undefined);
  useEffect(() => { api.get(`/admin/tester-invites/${inviteId}/legacy-package`).then((r) => setPkg(r.data)).catch(() => setPkg(null)); }, [inviteId, refreshKey]);
  if (!pkg) return null;
  const r = pkg.results || {};
  const cancel = async () => { try { await api.post(`/admin/tester-invites/${inviteId}/legacy-package/cancel`); toast.success("Pacchetto annullato"); onChanged && onChanged(); } catch (e) { toast.error(errText(e.response?.data?.detail)); } };
  return (
    <div className="mt-1.5 rounded-md bg-[#1A2023] border border-[#C9A227]/30 px-2.5 py-1.5" data-testid={`legacy-pkg-${inviteId}`}>
      <p className="text-[11px] text-[#C9A227] font-mono-label">Import legacy: {pkg.legacy_stable_id} · {PKG_STATUS[pkg.status] || pkg.status}</p>
      <p className="text-[11px] text-[#8FA396]">
        Candidati {pkg.candidate_count} · importabili {pkg.counts?.importable ?? "—"}
        {pkg.status === "completed" ? ` · importati ${r.imported_new_identity + r.associated_existing_identity} · saltati ${r.skipped_by_user} · da verificare ${r.needs_identity_review} · non validi ${r.invalid_candidate}` : ""}
      </p>
      {pkg.status !== "completed" && pkg.status !== "cancelled" && (
        <button data-testid={`legacy-pkg-cancel-${inviteId}`} onClick={cancel} className="text-[10px] text-red-300 hover:underline mt-0.5">Annulla pacchetto</button>
      )}
    </div>
  );
}

function TesterInvitesSection({ onRollbackDone }) {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ recipient_email: "", recipient_name: "", internal_note: "", invite_expires_in_days: 14, program_status: "tester", license_state: "trial", license_expires_at: "" });
  const [creating, setCreating] = useState(false);
  const [lastLink, setLastLink] = useState(null);
  const [rollback, setRollback] = useState(null);
  const [del, setDel] = useState(null);
  const [legacyFor, setLegacyFor] = useState(null);
  const [pkgRefresh, setPkgRefresh] = useState(0);
  const [sectionOpen, setSectionOpen] = useState(false);
  const [activeOpen, setActiveOpen] = useState(false);
  const [histOpen, setHistOpen] = useState(false);

  const load = async () => { setLoading(true); try { const { data } = await api.get("/admin/tester-invites"); setInvites(data); } catch { /* noop */ } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.recipient_email) { toast.error("Inserisci l'email destinatario"); return; }
    setCreating(true);
    try {
      const payload = { ...form, invite_expires_in_days: Number(form.invite_expires_in_days) || 14, license_expires_at: form.license_expires_at || null };
      const { data } = await api.post("/admin/tester-invites", payload);
      setLastLink(data.invite_link);
      toast.success("Invito creato");
      setForm({ recipient_email: "", recipient_name: "", internal_note: "", invite_expires_in_days: 14, program_status: "tester", license_state: "trial", license_expires_at: "" });
      load();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setCreating(false); }
  };
  const copy = (link) => { if (!link) { toast.error("Link non disponibile"); return; } navigator.clipboard?.writeText(link); toast.success("Link copiato"); };
  const resend = async (id) => { try { await api.post(`/admin/tester-invites/${id}/resend`); toast.success("Email reinviata"); load(); } catch (e) { toast.error(errText(e.response?.data?.detail)); load(); } };
  const revoke = async (id) => { try { await api.post(`/admin/tester-invites/${id}/revoke`); toast.success("Invito revocato"); load(); } catch (e) { toast.error(errText(e.response?.data?.detail)); } };
  const openRollback = async (id) => { try { const { data } = await api.get(`/admin/tester-invites/${id}/rollback-preview`); setRollback({ id, ...data }); } catch (e) { toast.error(errText(e.response?.data?.detail)); } };
  const doRollback = async () => { try { await api.post(`/admin/tester-invites/${rollback.id}/rollback`); toast.success("Onboarding eliminato"); setRollback(null); load(); onRollbackDone && onRollbackDone(); } catch (e) { toast.error(errText(e.response?.data?.detail)); } };
  const openDelete = async (id) => { try { const { data } = await api.get(`/admin/tester-invites/${id}/delete-preview`); setDel({ id, ...data }); } catch (e) { toast.error(errText(e.response?.data?.detail)); } };
  const doDelete = async () => { try { await api.delete(`/admin/tester-invites/${del.id}`); toast.success("Invito eliminato"); setDel(null); load(); onRollbackDone && onRollbackDone(); } catch (e) { toast.error(errText(e.response?.data?.detail)); } };
  const [emailEdit, setEmailEdit] = useState(null);
  const saveEmail = async () => { try { await api.put(`/admin/tester-invites/${emailEdit.id}/email`, { email: (emailEdit.email || "").trim() }); toast.success("Email invito aggiornata. Usa \"Reinvia email\" per avvisare il nuovo indirizzo."); setEmailEdit(null); load(); } catch (e) { toast.error(errText(e.response?.data?.detail)); } };

  const activeInvites = invites.filter((i) => i.status === "available");
  const historyInvites = invites.filter((i) => i.status !== "available");

  const renderInvite = (inv) => {
    const [lbl, cls] = INVITE_STATUS[inv.status] || ["—", "bg-neutral-500"];
    return (
      <div key={inv.id} className="rounded-md border border-[#333B3C] bg-[#1A2023] px-3 py-1.5 flex items-center justify-between gap-2 flex-wrap" data-testid={`invite-${inv.id}`}>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-sm font-medium text-[#EAE4D3] truncate">{inv.recipient_email}</span>
            <span className={`shrink-0 text-[10px] font-mono-label rounded-full px-2 py-0.5 text-white ${cls}`} data-testid={`invite-status-${inv.id}`}>{lbl}</span>
          </div>
          <p className="text-[11px] leading-snug text-[#8FA396] truncate">
            Program {inv.program_status} · Lic {inv.license_state} · scad. invito {fmtIT(inv.expires_at)}
            {inv.stable_name ? ` · Scuderia: ${inv.stable_name}` : ""}{inv.user_name ? ` · Gestore: ${inv.user_name}` : ""}
          </p>
          <p className="text-[11px] leading-snug truncate" data-testid={`invite-email-status-${inv.id}`}>
            <span className={inv.email_status === "sent" ? "text-emerald-400" : inv.email_status === "failed" ? "text-red-400" : "text-[#8FA396]"}>
              Email: {inv.email_status === "sent" ? "inviata" : inv.email_status === "failed" ? "invio fallito" : "non inviata"}
            </span>
            {inv.last_email_sent_at ? ` · ${fmtIT(inv.last_email_sent_at)}` : ""}
            {!inv.legacy_package_id && <span className="text-[#6F8377]"> · Nessun import legacy associato</span>}
          </p>
          {inv.legacy_package_id && <LegacyPackageInfo inviteId={inv.id} refreshKey={pkgRefresh} onChanged={() => { setPkgRefresh((k) => k + 1); load(); }} />}
        </div>
        {/* Desktop: inline action buttons */}
        <div className="hidden sm:flex items-center gap-1.5 shrink-0 flex-wrap">
          {inv.invite_link && (inv.status === "available") && <Button size="sm" variant="outline" data-testid={`copy-link-${inv.id}`} onClick={() => copy(inv.invite_link)} className="border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10">Copia link</Button>}
          {inv.status === "available" && inv.invite_link && <Button size="sm" variant="outline" data-testid={`resend-${inv.id}`} onClick={() => resend(inv.id)} className="border-sky-500 text-sky-300 hover:bg-sky-500/10">Reinvia email</Button>}
          {inv.can_attach_legacy && !inv.legacy_package_id && <Button size="sm" variant="outline" data-testid={`attach-legacy-${inv.id}`} onClick={() => setLegacyFor(inv.id)} className="border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10">Associa import legacy</Button>}
          {inv.status === "available" && <Button size="sm" variant="outline" data-testid={`edit-email-${inv.id}`} onClick={() => setEmailEdit({ id: inv.id, email: inv.recipient_email })} className="border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10">Modifica email</Button>}
          {inv.status === "available" && <Button size="sm" variant="outline" data-testid={`revoke-invite-${inv.id}`} onClick={() => revoke(inv.id)} className="border-neutral-400 text-neutral-200 hover:bg-neutral-500/10">Revoca</Button>}
          {inv.status === "used" && <Button size="sm" variant="outline" data-testid={`rollback-invite-${inv.id}`} onClick={() => openRollback(inv.id)} className="border-red-500 text-red-300 hover:bg-red-500/10">Elimina onboarding</Button>}
          <Button size="sm" variant="outline" data-testid={`delete-invite-${inv.id}`} onClick={() => openDelete(inv.id)} className="border-red-700 text-red-400 hover:bg-red-700/10">Elimina invito</Button>
        </div>
        {/* Mobile: compact "Azioni" menu — keeps the whole card within the viewport */}
        <div className="sm:hidden shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" data-testid={`invite-actions-${inv.id}`} className="border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10">
                <MoreHorizontal className="h-4 w-4 mr-1" /> Azioni
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-[#232A2C] border-[#333B3C] text-[#EAE4D3]">
              {inv.invite_link && inv.status === "available" && <DropdownMenuItem data-testid={`m-copy-link-${inv.id}`} onClick={() => copy(inv.invite_link)}>Copia link</DropdownMenuItem>}
              {inv.status === "available" && inv.invite_link && <DropdownMenuItem data-testid={`m-resend-${inv.id}`} onClick={() => resend(inv.id)}>Reinvia email</DropdownMenuItem>}
              {inv.can_attach_legacy && !inv.legacy_package_id && <DropdownMenuItem data-testid={`m-attach-legacy-${inv.id}`} onClick={() => setLegacyFor(inv.id)}>Associa import legacy</DropdownMenuItem>}
              {inv.status === "available" && <DropdownMenuItem data-testid={`m-edit-email-${inv.id}`} onClick={() => setEmailEdit({ id: inv.id, email: inv.recipient_email })}>Modifica email</DropdownMenuItem>}
              {inv.status === "available" && <DropdownMenuItem data-testid={`m-revoke-invite-${inv.id}`} onClick={() => revoke(inv.id)}>Revoca</DropdownMenuItem>}
              {inv.status === "used" && <DropdownMenuItem data-testid={`m-rollback-invite-${inv.id}`} onClick={() => openRollback(inv.id)} className="text-red-300">Elimina onboarding</DropdownMenuItem>}
              <DropdownMenuItem data-testid={`m-delete-invite-${inv.id}`} onClick={() => openDelete(inv.id)} className="text-red-400">Elimina invito</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  };

  return (
    <div className="mb-6 rounded-lg border border-[#333B3C] bg-[#232A2C] p-4" data-testid="tester-invites-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button type="button" data-testid="tester-invites-toggle" onClick={() => setSectionOpen((v) => !v)} className="flex items-center gap-2 text-left">
          <ChevronDown className={`h-4 w-4 shrink-0 text-[#8FA396] transition-transform ${sectionOpen ? "rotate-180" : ""}`} />
          <h2 className="font-display text-lg font-semibold">Inviti Tester <span className="text-[#8FA396]">({invites.length})</span></h2>
        </button>
        {sectionOpen && <Button size="sm" data-testid="new-invite-toggle" onClick={() => setOpen(!open)} className="bg-[#C9A227] text-[#1A2023] hover:bg-[#b8931f]">{open ? "Chiudi" : "Nuovo invito"}</Button>}
      </div>
      {open && (
        <div className="mt-3 grid sm:grid-cols-2 gap-2" data-testid="new-invite-form">
          <input data-testid="invite-email" placeholder="Email destinatario" value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} className="rounded-md bg-[#1A2023] border border-[#333B3C] text-sm px-2 py-1.5 text-[#EAE4D3]" />
          <input data-testid="invite-name" placeholder="Nome referente (opz.)" value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} className="rounded-md bg-[#1A2023] border border-[#333B3C] text-sm px-2 py-1.5 text-[#EAE4D3]" />
          <select data-testid="invite-program" value={form.program_status} onChange={(e) => setForm({ ...form, program_status: e.target.value })} className="rounded-md bg-[#1A2023] border border-[#333B3C] text-sm px-2 py-1.5 text-[#EAE4D3]">
            <option value="tester">Tester</option><option value="early_tester">Early Tester</option><option value="founder">Founder</option>
          </select>
          <select data-testid="invite-license" value={form.license_state} onChange={(e) => setForm({ ...form, license_state: e.target.value })} className="rounded-md bg-[#1A2023] border border-[#333B3C] text-sm px-2 py-1.5 text-[#EAE4D3]">
            <option value="trial">Licenza: Trial</option><option value="active">Licenza: Attiva</option>
          </select>
          <input data-testid="invite-license-exp" type="date" placeholder="Scadenza licenza" value={form.license_expires_at} onChange={(e) => setForm({ ...form, license_expires_at: e.target.value })} className="rounded-md bg-[#1A2023] border border-[#333B3C] text-sm px-2 py-1.5 text-[#EAE4D3]" />
          <input data-testid="invite-exp-days" type="number" min="1" placeholder="Scadenza invito (giorni)" value={form.invite_expires_in_days} onChange={(e) => setForm({ ...form, invite_expires_in_days: e.target.value })} className="rounded-md bg-[#1A2023] border border-[#333B3C] text-sm px-2 py-1.5 text-[#EAE4D3]" />
          <textarea data-testid="invite-note" placeholder="Nota interna (opz.)" value={form.internal_note} onChange={(e) => setForm({ ...form, internal_note: e.target.value })} className="sm:col-span-2 rounded-md bg-[#1A2023] border border-[#333B3C] text-sm px-2 py-1.5 text-[#EAE4D3]" rows={2} />
          <Button data-testid="invite-create" onClick={create} disabled={creating} className="sm:col-span-2 bg-[#2E6B4B] hover:bg-[#347D57]">{creating && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Crea invito</Button>
        </div>
      )}
      {lastLink && (
        <div className="mt-3 rounded-md bg-[#1A2023] border border-[#C9A227]/40 p-3 flex items-center justify-between gap-2 flex-wrap" data-testid="invite-link-box">
          <span className="text-xs text-[#EAE4D3] break-all">{lastLink}</span>
          <Button size="sm" data-testid="copy-invite-link" onClick={() => copy(lastLink)} className="bg-[#C9A227] text-[#1A2023] hover:bg-[#b8931f]">Copia link</Button>
        </div>
      )}
      <div className={`mt-3 space-y-3 ${sectionOpen ? "" : "hidden"}`} data-testid="invites-list">
        {loading && <Loader2 className="h-5 w-5 animate-spin text-[#C9A227]" />}
        {!loading && invites.length === 0 && <p className="text-sm text-[#8FA396]">Nessun invito.</p>}
        {!loading && invites.length > 0 && (
          <>
            <div>
              <button type="button" data-testid="invites-active-toggle" onClick={() => setActiveOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-2 rounded-md border border-[#333B3C] bg-[#1A2023] px-3 py-2 text-left hover:bg-[#232A2C] transition-colors">
                <span className="text-xs font-mono-label text-[#EAE4D3]">Inviti attivi <span className="text-[#8FA396]">({activeInvites.length})</span></span>
                <ChevronDown className={`h-4 w-4 shrink-0 text-[#8FA396] transition-transform ${activeOpen ? "rotate-180" : ""}`} />
              </button>
              {activeOpen && (
                <div className="mt-2 space-y-1.5" data-testid="invites-active-list">
                  {activeInvites.length === 0 ? <p className="text-xs text-[#8FA396] px-1 py-1">Nessun invito attivo.</p> : activeInvites.map(renderInvite)}
                </div>
              )}
            </div>
            <div>
              <button type="button" data-testid="invites-history-toggle" onClick={() => setHistOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-2 rounded-md border border-[#333B3C] bg-[#1A2023] px-3 py-2 text-left hover:bg-[#232A2C] transition-colors">
                <span className="text-xs font-mono-label text-[#EAE4D3]">Storico inviti <span className="text-[#8FA396]">({historyInvites.length})</span></span>
                <ChevronDown className={`h-4 w-4 shrink-0 text-[#8FA396] transition-transform ${histOpen ? "rotate-180" : ""}`} />
              </button>
              {histOpen && (
                <div className="mt-2 space-y-1.5" data-testid="invites-history-list">
                  {historyInvites.length === 0 ? <p className="text-xs text-[#8FA396] px-1 py-1">Nessun invito in storico.</p> : historyInvites.map(renderInvite)}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <Dialog open={!!emailEdit} onOpenChange={(o) => !o && setEmailEdit(null)}>
        <DialogContent data-testid="invite-email-dialog" className="bg-[#232A2C] border-[#333B3C] text-[#EAE4D3]">
          <DialogHeader><DialogTitle className="font-display">Modifica email invito</DialogTitle>
            <DialogDescription className="text-[#8FA396] text-xs">Correggi l'indirizzo di un invito non ancora utilizzato. Il link e la scadenza restano gli stessi; il vecchio indirizzo non potra piu completare l'onboarding. Non viene inviata alcuna email: usa poi "Reinvia email".</DialogDescription></DialogHeader>
          {emailEdit && (
            <input data-testid="invite-email-input" type="email" value={emailEdit.email}
              onChange={(e) => setEmailEdit({ ...emailEdit, email: e.target.value })}
              className="w-full rounded-md bg-[#1A2023] border border-[#333B3C] text-sm px-2 py-2 text-[#EAE4D3]" placeholder="nuova@email.it" />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailEdit(null)} data-testid="invite-email-cancel">Annulla</Button>
            <Button onClick={saveEmail} data-testid="invite-email-save" className="bg-[#2E6B4B] hover:bg-[#347D57]">Salva email</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>        <DialogContent data-testid="invite-delete-dialog" className="bg-[#232A2C] border-[#333B3C] text-[#EAE4D3]">
          <DialogHeader><DialogTitle className="font-display text-red-300">Eliminare l'invito Tester?</DialogTitle>
            <DialogDescription className="text-[#8FA396] text-xs">Rimuove definitivamente il record dell'invito. Il link diventa inutilizzabile. Non tocca account o scuderie.</DialogDescription></DialogHeader>
          {del && (
            <div className="text-sm space-y-2" data-testid="invite-delete-preview">
              <p className="text-[#EAE4D3]">{del.user_email}</p>
              {del.blocked ? (
                <div className="rounded-md bg-red-900/30 border border-red-500/40 p-2" data-testid="invite-delete-blocked">
                  <p className="text-red-300 text-xs">{del.note}</p>
                  {del.has_user && <p className="text-[11px] text-red-200">· Account esistente: {del.user_name}</p>}
                  {del.has_stable && <p className="text-[11px] text-red-200">· Scuderia esistente: {del.stable_name}</p>}
                </div>
              ) : (
                <p className="text-[#8FA396] text-xs">{del.note}</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDel(null)} data-testid="invite-delete-cancel">Annulla</Button>
            <Button onClick={doDelete} disabled={del?.blocked} data-testid="invite-delete-confirm" className="bg-red-600 hover:bg-red-700 text-white">Elimina invito</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!rollback} onOpenChange={(o) => !o && setRollback(null)}>
        <DialogContent data-testid="rollback-dialog" className="bg-[#232A2C] border-[#333B3C] text-[#EAE4D3]">
          <DialogHeader><DialogTitle className="font-display text-red-300">Eliminare onboarding e dati creati?</DialogTitle><DialogDescription className="text-[#8FA396] text-xs">Verranno rimosse solo le entità create da questo onboarding (per ID provenance). Operazione irreversibile.</DialogDescription></DialogHeader>
          {rollback && (
            <div className="text-sm space-y-2">
              <p className="text-[#EAE4D3]">Verranno eliminati (solo entità di questo onboarding, per ID):</p>
              <ul className="text-[#8FA396] list-disc pl-5" data-testid="rollback-preview">
                {(rollback.will_delete?.users || []).map((u) => <li key={u.id}>Account: {u.name} ({u.email})</li>)}
                {(rollback.will_delete?.stables || []).map((s) => <li key={s.id}>Scuderia: {s.name}</li>)}
                <li>Membership: {rollback.will_delete?.memberships} · Licenze: {rollback.will_delete?.licenses}</li>
                <li data-testid="rollback-horse-count">{rollback.will_delete?.horse_identities || 0} nuove Horse Identity create da questo import</li>
              </ul>
              {rollback.preserved_preexisting?.length > 0 && (
                <div className="rounded-md bg-sky-900/30 border border-sky-500/40 p-2" data-testid="rollback-preserved">
                  <p className="text-sky-200 text-xs font-semibold">Scollegate ma preservate (identità preesistenti):</p>
                  {rollback.preserved_preexisting.map((h, i) => <p key={i} className="text-[11px] text-sky-100">· {h.name}</p>)}
                </div>
              )}
              {rollback.blocked_referenced?.length > 0 && (
                <div className="rounded-md bg-amber-900/30 border border-amber-500/40 p-2" data-testid="rollback-blocked-ref">
                  <p className="text-amber-200 text-xs font-semibold">Non eliminabili automaticamente (riferimenti esterni):</p>
                  {rollback.blocked_referenced.map((h, i) => <p key={i} className="text-[11px] text-amber-100">· {h.name} ({h.refs} riferimenti)</p>)}
                </div>
              )}
              {rollback.blockers?.length > 0 && (
                <div className="rounded-md bg-red-900/30 border border-red-500/40 p-2" data-testid="rollback-blockers">
                  <p className="text-red-300 text-xs font-semibold">Dati esterni presenti: rollback bloccato.</p>
                  {rollback.blockers.map((b, i) => <p key={i} className="text-[11px] text-red-200">· {b.type} ({b.count})</p>)}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRollback(null)} data-testid="rollback-cancel">Annulla</Button>
            <Button onClick={doRollback} disabled={(rollback?.blockers?.length > 0) || (rollback?.blocked_referenced?.length > 0) || (rollback?.preserved_preexisting?.length > 0)} data-testid="rollback-confirm" className="bg-red-600 hover:bg-red-700 text-white">Elimina definitivamente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {legacyFor && <LegacyAttachDialog inviteId={legacyFor} onClose={() => setLegacyFor(null)} onDone={() => { setLegacyFor(null); setPkgRefresh((k) => k + 1); load(); }} />}
    </div>
  );
}

function StatusBadge({ s }) {
  const norm = (s === "deactivated" || s === "deleted") ? "archived" : (s || "active");
  const M = { active: ["Attiva", "bg-emerald-600"], archived: ["Archiviata", "bg-neutral-500"], suspended: ["Sospesa", "bg-amber-500"] };
  const [l, c] = M[norm] || M.active;
  return <span data-testid="stable-status-badge" className={`text-[10px] font-mono-label rounded-full px-2 py-0.5 text-white ${c}`}>{l}</span>;
}

function ReqStatus({ s }) {
  const M = { pending: ["Pendente", "text-amber-300"], executed: ["Eseguita", "text-emerald-300"], dismissed: ["Chiusa", "text-neutral-400"] };
  const [l, c] = M[s] || M.pending;
  return <span className={`text-[10px] font-mono-label ${c}`}>· {l}</span>;
}

function Field({ label, value, mono }) {
  return (
    <div>
      <span className="text-[#6F8377] text-xs">{label}: </span>
      <span className={mono ? "font-mono-label text-xs" : ""}>{value || "—"}</span>
    </div>
  );
}

function RequestCard({ title, testid, empty, children }) {
  const items = Array.isArray(children) ? children : (children ? [children] : []);
  const has = items.some(Boolean);
  return (
    <div className="rounded-lg border border-[#333B3C] bg-[#232A2C] p-5" data-testid={testid}>
      <h3 className="font-display font-semibold mb-2">{title}</h3>
      {has ? <div className="divide-y divide-[#333B3C]">{children}</div>
        : <p className="text-sm text-[#8FA396]">{empty}</p>}
    </div>
  );
}

const LIC = {
  trial: ["Prova", "bg-sky-600"], active: ["Attiva", "bg-emerald-600"],
  expired: ["Scaduta", "bg-red-600"], suspended: ["Sospesa", "bg-amber-500"],
  missing: ["Nessuna", "bg-neutral-600"],
};

function LicenseBadge({ status }) {
  const [l, c] = LIC[status || "missing"] || LIC.missing;
  return <span data-testid="license-status-badge" className={`text-[10px] font-mono-label rounded-full px-2 py-0.5 text-white ${c}`}>Lic. {l}</span>;
}

function LicensePanel({ stableId, license, onChanged }) {
  const [status, setStatus] = useState(license?.status || "active");
  const [expires, setExpires] = useState((license?.expires_at || "").slice(0, 10));
  const [notes, setNotes] = useState(license?.notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      if (license) {
        await api.put(`/admin/licenses/${license.id}`, { status, expires_at: expires || null, notes });
        toast.success("Licenza aggiornata");
      } else {
        await api.post("/admin/licenses", { stable_id: stableId, status, expires_at: expires || null, notes });
        toast.success("Licenza creata");
      }
      onChanged();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid={`license-panel-${stableId}`}>
      <p className="font-mono-label text-[10px] text-[#C9A227] mb-1">Licenza</p>
      {license ? (
        <p className="text-xs text-[#8FA396] mb-2">
          ID <span className="font-mono-label">{license.id.slice(0, 8)}…</span> · stato effettivo <LicenseBadge status={license.effective_status} />
          {license.activated_at ? ` · attivata ${fmtIT(license.activated_at)}` : ""}
        </p>
      ) : (
        <p className="text-xs text-amber-300 mb-2">Questa scuderia non ha ancora una licenza. Creane una per renderla operativa.</p>
      )}
      <div className="grid sm:grid-cols-3 gap-2 items-end">
        <label className="text-xs text-[#6F8377]">Stato
          <select data-testid={`license-status-select-${stableId}`} value={status} onChange={(e) => setStatus(e.target.value)}
            className="mt-1 w-full bg-[#232A2C] border border-[#333B3C] rounded-md px-2 py-1.5 text-sm text-[#EAE4D3]">
            <option value="trial">Prova (Trial)</option>
            <option value="active">Attiva</option>
            <option value="suspended">Sospesa</option>
            <option value="expired">Scaduta</option>
          </select>
        </label>
        <div className="text-xs text-[#6F8377]">Scadenza (opzionale)
          <DateField testId={`license-expires-${stableId}`} value={expires} onChange={setExpires}
            className="mt-1 bg-[#232A2C] border-[#333B3C] text-[#EAE4D3] hover:bg-[#2A3234] hover:text-[#EAE4D3]" />
        </div>
        <Button size="sm" onClick={save} disabled={saving} data-testid={`license-save-${stableId}`}
          className="bg-[#C9A227] hover:bg-[#b8941f] text-[#1A2023]">{license ? "Salva licenza" : "Crea licenza"}</Button>
      </div>
      <input placeholder="Note interne Platform Admin" value={notes} onChange={(e) => setNotes(e.target.value)}
        data-testid={`license-notes-${stableId}`}
        className="mt-2 w-full bg-[#232A2C] border border-[#333B3C] rounded-md px-2 py-1.5 text-sm text-[#EAE4D3] placeholder:text-[#6F8377]" />
    </div>
  );
}

function AvailableLicenseForm({ onCreated }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("trial");
  const [expires, setExpires] = useState("");
  const [saving, setSaving] = useState(false);
  const create = async () => {
    if (!email.trim()) { toast.error("Inserisci l'email del Gestore"); return; }
    setSaving(true);
    try {
      await api.post("/admin/licenses", { assigned_to_email: email.trim(), status, expires_at: expires || null });
      toast.success("Licenza disponibile creata");
      setEmail(""); setExpires(""); setStatus("trial"); onCreated();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="rounded-lg border border-[#333B3C] bg-[#232A2C] p-4 grid sm:grid-cols-4 gap-2 items-end" data-testid="avail-license-form">
      <label className="text-xs text-[#6F8377]">Email Gestore
        <input data-testid="avail-license-email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="gestore@esempio.it"
          className="mt-1 w-full bg-[#1A2023] border border-[#333B3C] rounded-md px-2 py-1.5 text-sm text-[#EAE4D3] placeholder:text-[#6F8377]" />
      </label>
      <label className="text-xs text-[#6F8377]">Stato
        <select data-testid="avail-license-status" value={status} onChange={(e) => setStatus(e.target.value)}
          className="mt-1 w-full bg-[#1A2023] border border-[#333B3C] rounded-md px-2 py-1.5 text-sm text-[#EAE4D3]">
          <option value="trial">Prova (Trial)</option>
          <option value="active">Attiva</option>
        </select>
      </label>
      <div className="text-xs text-[#6F8377]">Scadenza (opzionale)
        <DateField testId="avail-license-expires" value={expires} onChange={setExpires}
          className="mt-1 bg-[#1A2023] border-[#333B3C] text-[#EAE4D3] hover:bg-[#232A2C] hover:text-[#EAE4D3]" />
      </div>
      <Button onClick={create} disabled={saving} data-testid="avail-license-create"
        className="bg-[#C9A227] hover:bg-[#b8941f] text-[#1A2023]">Crea licenza</Button>
    </div>
  );
}


function DuplicateStablesSection() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/stable-duplicates")
      .then((r) => setGroups(r.data.groups || []))
      .catch(() => setGroups([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const resolve = async (g, status) => {
    try {
      await api.post("/admin/stable-duplicates/resolve", { pair_key: g.pair_key, signature: g.signature, status });
      toast.success(status === "not_duplicate" ? "Coppia segnata come non duplicata" : "Coppia segnata come «Da gestire»");
      load();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
  };

  return (
    <section data-testid="admin-duplicates-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="font-display text-lg font-semibold">Possibili duplicati <span className="text-[#8FA396]">({groups.length})</span></h2>
        <Button size="sm" variant="outline" data-testid="dup-rescan" onClick={load}
          className="border-[#333B3C] text-[#EAE4D3] hover:bg-[#2E6B4B]">
          <RefreshCw className="h-4 w-4 mr-2" /> Riscansiona
        </Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#C9A227]" /></div>
      ) : groups.length === 0 ? (
        <p className="text-sm text-[#8FA396]">Nessun possibile duplicato rilevato.</p>
      ) : (
        <div className="space-y-3">{groups.map((g) => <DupGroup key={g.pair_key} g={g} onResolve={resolve} />)}</div>
      )}
    </section>
  );
}

function DupGroup({ g, onResolve }) {
  return (
    <div data-testid={`dup-group-${g.pair_key}`} className="rounded-lg border border-[#333B3C] bg-[#232A2C] p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {g.status === "to_manage" && (
          <span data-testid={`dup-flag-${g.pair_key}`}
            className="text-[10px] font-mono-label uppercase rounded-full px-2 py-0.5 bg-amber-500 text-[#1A2023]">Da gestire</span>
        )}
        <span className="text-[11px] text-[#8FA396]">Segnalata per:</span>
        {g.reasons.map((r, i) => (
          <span key={i} className="text-[11px] rounded-full px-2 py-0.5 border border-[#C9A227]/50 text-[#C9A227]">
            {r.label}: {r.value}
          </span>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {g.stables.map((s) => (
          <div key={s.id} className="rounded-md border border-[#333B3C] bg-[#1A2023] p-3">
            <p className="font-semibold truncate">{s.name}</p>
            <p className="text-xs text-[#8FA396] truncate">{s.denominazione || "—"}{s.comune ? ` · ${s.comune}` : ""}</p>
            <div className="mt-1.5 text-[11px] text-[#8FA396] space-y-0.5">
              {s.partita_iva && <p>P.IVA: {s.partita_iva}</p>}
              {s.codice_fiscale && <p>CF: {s.codice_fiscale}</p>}
              {s.asl_code && <p>Cod. stalla: {s.asl_code}</p>}
              <p className="font-mono-label text-[9px] opacity-70">ID {s.id.slice(0, 8)}…</p>
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" data-testid={`dup-not-${g.pair_key}`} onClick={() => onResolve(g, "not_duplicate")}
          className="border-emerald-500 text-emerald-300 hover:bg-emerald-500/10">Non sono duplicate</Button>
        {g.status !== "to_manage" && (
          <Button size="sm" variant="outline" data-testid={`dup-manage-${g.pair_key}`} onClick={() => onResolve(g, "to_manage")}
            className="border-amber-500 text-amber-300 hover:bg-amber-500/10">Da gestire</Button>
        )}
      </div>
    </div>
  );
}

function GlobalHorseSearchSection({ onAssist }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [dups, setDups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setItems([]); setDups([]); setSearched(false); return; }
    const t = setTimeout(() => {
      setLoading(true); setSearched(true);
      api.get("/admin/horses/search", { params: { q: term } })
        .then((r) => { setItems(r.data.items || []); setDups(r.data.duplicates || []); })
        .catch(() => { setItems([]); setDups([]); })
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const dupIds = new Set(dups.flatMap((d) => d.horse_ids));

  return (
    <section data-testid="admin-horse-search-section">
      <h2 className="font-display text-lg font-semibold mb-1">Ricerca globale cavalli</h2>
      <p className="text-xs text-[#8FA396] mb-3">Cerca per microchip, UELN o nome tra tutte le scuderie.</p>
      <div className="relative w-full sm:w-96 mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#C9A227]" />
        <input data-testid="global-horse-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Microchip, UELN o nome…"
          className="w-full pl-9 pr-3 py-2 rounded-md bg-[#232A2C] border border-[#333B3C] text-sm outline-none placeholder:text-[#6F8377]" />
      </div>

      {dups.length > 0 && (
        <div data-testid="global-horse-anomalies" className="mb-3 rounded-md border border-amber-500/50 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
          <p className="font-medium mb-1">⚠ Identificativi duplicati rilevati</p>
          {dups.map((d, i) => (
            <p key={i} className="text-xs">{d.label} <span className="font-mono-label">{d.value}</span> presente su {d.horse_ids.length} cavalli.</p>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#C9A227]" /></div>
      ) : searched && items.length === 0 ? (
        <p className="text-sm text-[#8FA396]">Nessun cavallo trovato.</p>
      ) : items.length > 0 ? (
        <div className="rounded-lg border border-[#333B3C] bg-[#232A2C] divide-y divide-[#333B3C]">
          {items.map((h) => (
            <div key={h.id} data-testid={`global-horse-${h.id}`}
              className={`flex items-center gap-3 px-4 py-2.5 flex-wrap ${dupIds.has(h.id) ? "bg-amber-500/5" : ""}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">
                  {h.display_name}
                  {h.archived && <span className="ml-2 text-[10px] font-mono-label uppercase rounded-full px-1.5 py-0.5 bg-[#2E6B4B] text-[#8FA396]">Archiviato</span>}
                  {dupIds.has(h.id) && <span className="ml-2 text-[10px] font-mono-label uppercase rounded-full px-1.5 py-0.5 bg-amber-500 text-[#1A2023]">Duplicato</span>}
                </p>
                <p className="text-xs text-[#8FA396] truncate">
                  {h.microchip ? `Microchip ${h.microchip}` : "Microchip —"}{h.ueln ? ` · UELN ${h.ueln}` : ""}
                </p>
                <p className="text-xs text-[#8FA396] truncate">Scuderia: <span className="text-[#EAE4D3]">{h.stable_name}</span></p>
              </div>
              <Button size="sm" variant="outline" data-testid={`global-horse-open-${h.id}`} onClick={() => onAssist(h.stable_id)}
                className="border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10 shrink-0">Apri in assistenza</Button>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}


function DictionariesSection() {
  const [kind, setKind] = useState("breeds");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [counts, setCounts] = useState({ breeds: 0, coats: 0, total: 0 });

  // Merge dialog: { source: term, target: {id,name,usage_count}|null }
  const [merge, setMerge] = useState(null);
  const [mQ, setMQ] = useState("");
  const [mResults, setMResults] = useState([]);
  const [mLoading, setMLoading] = useState(false);
  const [merging, setMerging] = useState(false);

  const loadCounts = useCallback(() => {
    api.get("/admin/dictionaries-pending-count").then((r) => setCounts(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/dictionaries/${kind}`, { params: { q, status: statusFilter } })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [kind, q, statusFilter]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  const openMerge = (source, presetTarget = null) => {
    setMerge({ source, target: presetTarget });
    setMQ("");
    setMResults([]);
  };
  const closeMerge = () => { setMerge(null); setMQ(""); setMResults([]); };

  // Searchable target dropdown (excludes the source term itself).
  useEffect(() => {
    if (!merge || merge.target) return;
    const t = setTimeout(() => {
      setMLoading(true);
      api.get(`/admin/dictionaries/${kind}`, { params: { q: mQ } })
        .then((r) => setMResults((r.data || []).filter((x) => x.id !== merge.source.id)))
        .catch(() => setMResults([]))
        .finally(() => setMLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [merge, mQ, kind]);

  const saveRename = async (t) => {
    const name = draft.trim();
    if (!name) return;
    try {
      await api.put(`/admin/dictionaries/${kind}/${t.id}`, { name });
      toast.success("Voce rinominata");
      setEditing(null); setDraft(""); load(); loadCounts();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d?.code === "term_exists") {
        setEditing(null); setDraft("");
        openMerge(t, { id: d.target_id, name: d.target_name });
        return;
      }
      toast.error(errText(d));
    }
  };

  const setStatus = async (t, status) => {
    try {
      await api.put(`/admin/dictionaries/${kind}/${t.id}`, { name: t.name, status });
      if (status === "approved") toast.success("Voce approvata");
      load(); loadCounts();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
  };

  const doMerge = async () => {
    if (!merge?.target) return;
    setMerging(true);
    try {
      const { data } = await api.post(`/admin/dictionaries/${kind}/${merge.source.id}/merge`, { target_id: merge.target.id });
      toast.success(`Voci unite — ${data.reassigned} cavall${data.reassigned === 1 ? "o" : "i"} riassegnat${data.reassigned === 1 ? "o" : "i"}`);
      closeMerge(); load(); loadCounts();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setMerging(false); }
  };

  const FILTERS = [["", "Tutte"], ["pending", "Da revisionare"], ["approved", "Approvate"]];

  return (
    <section data-testid="admin-dictionaries-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="font-display text-lg font-semibold">Dizionari</h2>
        <div className="flex items-center gap-2">
          {[["breeds", "Razze"], ["coats", "Mantelli"]].map(([k, l]) => (
            <button key={k} data-testid={`dict-tab-${k}`} onClick={() => { setKind(k); setItems([]); }}
              className={`relative text-sm rounded-full px-3 py-1 border transition-colors ${
                kind === k ? "bg-[#C9A227] text-[#1A2023] border-[#C9A227]" : "bg-[#232A2C] text-[#EAE4D3] border-[#333B3C] hover:border-[#C9A227]"}`}>
              {l}
              {counts[k] > 0 && (
                <span data-testid={`dict-tab-badge-${k}`}
                  className={`ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
                    kind === k ? "bg-[#1A2023] text-[#C9A227]" : "bg-[#C9A227] text-[#1A2023]"}`}>
                  {counts[k]}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#C9A227]" />
          <input data-testid="dict-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca voce…"
            className="w-full pl-9 pr-3 py-2 rounded-md bg-[#232A2C] border border-[#333B3C] text-sm outline-none placeholder:text-[#6F8377]" />
        </div>
        <div className="flex items-center gap-1.5">
          {FILTERS.map(([v, l]) => (
            <button key={v || "all"} data-testid={`dict-filter-${v || "all"}`} onClick={() => setStatusFilter(v)}
              className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${
                statusFilter === v ? "bg-[#2E6B4B] text-[#EAE4D3] border-[#3a5745]" : "bg-transparent text-[#8FA396] border-[#333B3C] hover:text-[#EAE4D3]"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-[#333B3C] bg-[#232A2C] divide-y divide-[#333B3C]">
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#C9A227]" /></div>
        ) : items.length === 0 ? (
          <p className="text-sm text-[#8FA396] px-4 py-6">Nessuna voce nel dizionario.</p>
        ) : items.map((t) => (
          <div key={t.id} data-testid={`dict-row-${t.id}`} className="flex items-center gap-3 px-4 py-2.5 flex-wrap">
            {editing === t.id ? (
              <>
                <Input data-testid={`dict-rename-input-${t.id}`} value={draft} onChange={(e) => setDraft(e.target.value)}
                  className="h-8 bg-[#1A2023] border-[#333B3C] text-[#EAE4D3] flex-1 min-w-[140px]" />
                <Button size="sm" data-testid={`dict-rename-save-${t.id}`} onClick={() => saveRename(t)}
                  className="bg-[#C9A227] hover:bg-[#b8941f] text-[#1A2023]">Salva</Button>
                <Button size="sm" variant="ghost" onClick={() => { setEditing(null); setDraft(""); }} className="text-[#8FA396]">Annulla</Button>
              </>
            ) : (
              <>
                <span className="flex-1 min-w-0 truncate font-medium">{t.name}</span>
                <span data-testid={`dict-status-${t.id}`}
                  className={`text-[10px] font-mono-label uppercase rounded-full px-2 py-0.5 border ${
                    t.status === "approved" ? "text-[#7BC47F] border-[#347D57]" : "text-[#C9A227] border-[#333B3C]"}`}>
                  {t.status === "approved" ? "Approvata" : "Da revisionare"}
                </span>
                <span className="text-xs text-[#8FA396] w-16 text-right">{t.usage_count} usi</span>
                {t.status !== "approved" ? (
                  <Button size="sm" data-testid={`dict-approve-${t.id}`} onClick={() => setStatus(t, "approved")}
                    className="bg-[#347D57] hover:bg-[#3a5745] text-[#EAE4D3] h-8">Approva</Button>
                ) : (
                  <Button size="sm" variant="ghost" data-testid={`dict-toreview-${t.id}`} onClick={() => setStatus(t, "pending")}
                    className="text-[#8FA396] h-8">Rivedi</Button>
                )}
                <Button size="sm" variant="ghost" data-testid={`dict-merge-${t.id}`}
                  onClick={() => openMerge(t)} className="text-[#C9A227] h-8">Unisci</Button>
                <Button size="sm" variant="ghost" data-testid={`dict-rename-${t.id}`}
                  onClick={() => { setEditing(t.id); setDraft(t.name); }} className="text-[#EAE4D3] h-8">Rinomina</Button>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Merge dialog */}
      <Dialog open={!!merge} onOpenChange={(o) => { if (!o) closeMerge(); }}>
        <DialogContent data-testid="dict-merge-dialog" className="bg-[#1A2023] border-[#333B3C] text-[#EAE4D3]">
          <DialogHeader>
            <DialogTitle>Unisci voci del dizionario</DialogTitle>
            <DialogDescription className="text-[#8FA396]">
              Seleziona la voce canonica di destinazione. La voce sorgente verrà conservata come alias e tutti i cavalli collegati saranno riassegnati.
            </DialogDescription>
          </DialogHeader>

          {merge && (
            <div className="space-y-4">
              <div className="text-sm">
                <span className="text-[#8FA396]">Sorgente: </span>
                <span className="font-medium">{merge.source.name}</span>
                <span className="text-[#8FA396]"> · {merge.source.usage_count ?? 0} usi</span>
              </div>

              {!merge.target ? (
                <div>
                  <label className="text-xs text-[#8FA396] mb-1 block">Destinazione</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#C9A227]" />
                    <input data-testid="dict-merge-target-search" value={mQ} onChange={(e) => setMQ(e.target.value)}
                      autoFocus placeholder="Cerca la voce di destinazione…"
                      className="w-full pl-9 pr-3 py-2 rounded-md bg-[#232A2C] border border-[#333B3C] text-sm outline-none placeholder:text-[#6F8377]" />
                  </div>
                  <div className="mt-2 max-h-56 overflow-y-auto rounded-md border border-[#333B3C] divide-y divide-[#333B3C]">
                    {mLoading ? (
                      <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-[#C9A227]" /></div>
                    ) : mResults.length === 0 ? (
                      <p className="text-xs text-[#8FA396] px-3 py-3">Nessuna voce trovata.</p>
                    ) : mResults.map((r) => (
                      <button key={r.id} data-testid={`dict-merge-target-${r.id}`}
                        onClick={() => setMerge((m) => ({ ...m, target: r }))}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-[#232A2C] transition-colors">
                        <span className="truncate">{r.name}
                          {r.status === "approved" && <span className="ml-2 text-[10px] text-[#7BC47F]">approvata</span>}
                        </span>
                        <span className="text-xs text-[#8FA396] shrink-0">{r.usage_count} usi</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div data-testid="dict-merge-preview" className="rounded-md border border-[#333B3C] bg-[#232A2C] p-4 space-y-3">
                  <div className="flex items-center justify-center gap-3 text-base font-semibold">
                    <span className="text-[#C9A227]">{merge.source.name}</span>
                    <ArrowLeft className="h-4 w-4 rotate-180 text-[#8FA396]" />
                    <span className="text-[#7BC47F]">{merge.target.name}</span>
                  </div>
                  <p className="text-sm text-center text-[#8FA396]">
                    <strong className="text-[#EAE4D3]">{merge.source.usage_count ?? 0}</strong> cavall{(merge.source.usage_count ?? 0) === 1 ? "o" : "i"} verrann
                    {(merge.source.usage_count ?? 0) === 1 ? "o" : "o"} riassegnat{(merge.source.usage_count ?? 0) === 1 ? "o" : "i"} a «{merge.target.name}».
                  </p>
                  <p className="text-xs text-center text-[#8FA396]">
                    «{merge.source.name}» verrà conservato come <strong className="text-[#EAE4D3]">alias</strong> di «{merge.target.name}».
                  </p>
                  <div className="text-center">
                    <button data-testid="dict-merge-change-target" onClick={() => setMerge((m) => ({ ...m, target: null }))}
                      className="text-xs text-[#C9A227] hover:underline">Cambia destinazione</button>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={closeMerge} className="text-[#8FA396]">Annulla</Button>
            <Button data-testid="dict-merge-confirm" onClick={doMerge} disabled={!merge?.target || merging}
              className="bg-[#C9A227] hover:bg-[#b8941f] text-[#1A2023]">
              {merging ? <Loader2 className="h-4 w-4 animate-spin" /> : "Conferma unione"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}


function TrialDaysControl() {
  const [days, setDays] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setDays(String(r.data.trial_days))).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", { trial_days: parseInt(days, 10) || 30 });
      setDays(String(data.trial_days));
      toast.success("Durata trial aggiornata");
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-3 flex items-center gap-2 rounded-lg border border-[#333B3C] bg-[#232A2C] px-3 py-2" data-testid="trial-days-control">
      <span className="text-sm text-[#EAE4D3]">Durata Trial (giorni):</span>
      <Input data-testid="trial-days-input" type="number" min={1} value={days} onChange={(e) => setDays(e.target.value)}
        className="h-8 w-24 bg-[#1A2023] border-[#333B3C] text-[#EAE4D3]" />
      <Button size="sm" data-testid="trial-days-save" onClick={save} disabled={saving}
        className="bg-[#C9A227] hover:bg-[#b8941f] text-[#1A2023]">Salva</Button>
    </div>
  );
}


// ---------------------------------------------------------------------------
// FEEDBACK (Platform Admin)
// ---------------------------------------------------------------------------
const FB_STATUS = [
  ["new", "Nuovo"], ["reviewing", "In esame"], ["planned", "Pianificato"], ["completed", "Completato"], ["closed", "Chiuso"],
];
const FB_STATUS_LABEL = Object.fromEntries(FB_STATUS);
const fbTypeMeta = { bug: { label: "Bug", icon: Bug, cls: "text-red-300" }, suggestion: { label: "Suggerimento", icon: Lightbulb, cls: "text-[#C9A227]" } };

function FeedbackAdminSection() {
  const [items, setItems] = useState([]);
  const [typeF, setTypeF] = useState("all");
  const [statusF, setStatusF] = useState("all");
  const [roadmapF, setRoadmapF] = useState("all");
  const [roadmapItems, setRoadmapItems] = useState([]);
  const [sel, setSel] = useState(null);
  const rmMap = Object.fromEntries(roadmapItems.map((r) => [r.id, r.title]));

  useEffect(() => { api.get("/admin/roadmap").then((r) => setRoadmapItems(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const h = (e) => { if (e.detail?.id) setRoadmapF(e.detail.id); };
    window.addEventListener("equiro-fb-roadmap-filter", h);
    return () => window.removeEventListener("equiro-fb-roadmap-filter", h);
  }, []);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (typeF !== "all") p.set("type", typeF);
    if (statusF !== "all") p.set("status", statusF);
    if (roadmapF !== "all") p.set("roadmap_item_id", roadmapF);
    api.get(`/admin/feedback?${p.toString()}`).then((r) => setItems(r.data)).catch(() => {});
  }, [typeF, statusF, roadmapF]);
  useEffect(() => { load(); }, [load]);

  return (
    <section data-testid="admin-feedback-section" id="admin-feedback-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <h2 className="font-display text-lg font-semibold flex items-center gap-2"><MessageSquare className="h-5 w-5 text-[#C9A227]" /> Feedback <span className="text-[#8FA396]">({items.length})</span></h2>
        <div className="flex flex-wrap gap-2">
          <select data-testid="feedback-filter-type" value={typeF} onChange={(e) => setTypeF(e.target.value)} className="rounded-md bg-[#232A2C] border border-[#333B3C] text-xs px-2 py-1.5 text-[#EAE4D3]">
            <option value="all">Tipo: tutti</option><option value="bug">Bug</option><option value="suggestion">Suggerimento</option>
          </select>
          <select data-testid="feedback-filter-status" value={statusF} onChange={(e) => setStatusF(e.target.value)} className="rounded-md bg-[#232A2C] border border-[#333B3C] text-xs px-2 py-1.5 text-[#EAE4D3]">
            <option value="all">Stato: tutti</option>{FB_STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select data-testid="feedback-filter-roadmap" value={roadmapF} onChange={(e) => setRoadmapF(e.target.value)} className="rounded-md bg-[#232A2C] border border-[#333B3C] text-xs px-2 py-1.5 text-[#EAE4D3] max-w-[190px]">
            <option value="all">In arrivo: tutti</option>{roadmapItems.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
          </select>
        </div>
      </div>
      <div className="rounded-lg border border-[#333B3C] bg-[#232A2C] overflow-hidden">
        {items.length === 0 ? (
          <p className="text-sm text-[#8FA396] p-4" data-testid="feedback-empty">Nessun feedback.</p>
        ) : items.map((f) => {
          const Icon = (fbTypeMeta[f.type] || fbTypeMeta.bug).icon;
          return (
            <button key={f.id} data-testid={`feedback-row-${f.id}`} onClick={() => setSel(f)}
              className="w-full text-left flex items-center gap-3 px-4 py-2.5 border-b border-[#333B3C] last:border-0 hover:bg-[#1B3B2B] transition-colors">
              <Icon className={`h-4 w-4 shrink-0 ${(fbTypeMeta[f.type] || fbTypeMeta.bug).cls}`} />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium truncate">{f.title}</span>
                <span className="block text-[11px] text-[#8FA396] truncate">{f.stable_name || "—"} · {f.user_name || f.user_email || "—"} · {fmtIT(f.created_at)}{f.roadmap_item_id && rmMap[f.roadmap_item_id] ? ` · In arrivo: ${rmMap[f.roadmap_item_id]}` : ""}</span>
              </span>
              <span className="text-[10px] font-mono-label rounded-full px-2 py-0.5 bg-[#2E6B4B] text-[#EAE4D3] shrink-0" data-testid={`feedback-status-${f.id}`}>{FB_STATUS_LABEL[f.status] || f.status}</span>
            </button>
          );
        })}
      </div>
      <FeedbackDetailDialog item={sel} onClose={() => setSel(null)} onSaved={load} />
    </section>
  );
}

function FeedbackDetailDialog({ item, onClose, onSaved }) {
  const [status, setStatus] = useState("new");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (item) { setStatus(item.status || "new"); setNote(item.internal_note || ""); } }, [item]);
  if (!item) return null;
  const ctx = item.technical_context || {};
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/feedback/${item.id}`, { status, internal_note: note });
      toast.success("Feedback aggiornato"); onSaved(); onClose();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-[#1A2023] border-[#333B3C] text-[#EAE4D3]" data-testid="feedback-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">{(fbTypeMeta[item.type] || fbTypeMeta.bug).label}: {item.title}</DialogTitle>
          <DialogDescription className="text-xs text-[#8FA396]">{item.stable_name || "—"} · {item.user_name || item.user_email || "—"} ({item.role}) · {fmtIT(item.created_at)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div>
            <p className="font-mono-label text-[10px] text-[#C9A227] mb-1">Descrizione</p>
            <p className="whitespace-pre-wrap" data-testid="feedback-detail-desc">{item.description}</p>
          </div>
          {item.attempted_action && (
            <div><p className="font-mono-label text-[10px] text-[#C9A227] mb-1">Cosa stava cercando di fare</p><p className="whitespace-pre-wrap">{item.attempted_action}</p></div>
          )}
          <div>
            <p className="font-mono-label text-[10px] text-[#C9A227] mb-1">Contesto tecnico</p>
            <div className="rounded-lg bg-[#232A2C] border border-[#333B3C] p-2 text-[11px] text-[#8FA396] space-y-0.5" data-testid="feedback-detail-context">
              {Object.entries(ctx).map(([k, v]) => <div key={k}><span className="text-[#EAE4D3]">{k}:</span> {String(v)}</div>)}
            </div>
          </div>
          <div>
            <p className="font-mono-label text-[10px] text-[#C9A227] mb-1">Timeline stato</p>
            <div className="space-y-0.5 text-[11px] text-[#8FA396]" data-testid="feedback-detail-timeline">
              {(item.status_history || []).map((h, i) => <div key={i}>{FB_STATUS_LABEL[h.status] || h.status} · {fmtIT(h.at)}</div>)}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <p className="font-mono-label text-[10px] text-[#C9A227] mb-1">Stato</p>
              <select data-testid="feedback-detail-status" value={status} onChange={(e) => setStatus(e.target.value)} className="w-full rounded-md bg-[#232A2C] border border-[#333B3C] text-sm px-2 py-2 text-[#EAE4D3]">
                {FB_STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <div>
            <p className="font-mono-label text-[10px] text-[#C9A227] mb-1">Nota interna (solo Platform Admin)</p>
            <textarea data-testid="feedback-detail-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3}
              className="w-full rounded-md bg-[#232A2C] border border-[#333B3C] text-sm px-3 py-2 text-[#EAE4D3] outline-none" placeholder="Annotazioni non visibili all'utente" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#333B3C] text-[#EAE4D3] hover:bg-[#2E6B4B]">Chiudi</Button>
          <Button onClick={save} disabled={busy} data-testid="feedback-detail-save" className="bg-[#C9A227] hover:bg-[#b8941f] text-[#1A2023]">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// RELEASE NOTES / NOVITÀ (Platform Admin)
// ---------------------------------------------------------------------------
function CollapsibleSection({ title, testid, defaultOpen = true, danger = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section data-testid={testid}>
      <button type="button" data-testid={`${testid}-toggle`} onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center justify-between gap-2 rounded-lg border px-4 py-3 text-left transition-colors ${danger ? "border-red-800/60 bg-[#2a1414] hover:bg-[#331818]" : "border-[#333B3C] bg-[#232A2C] hover:bg-[#1a3527]"}`}>
        <span className={`font-display text-sm font-semibold ${danger ? "text-red-300" : "text-[#EAE4D3]"}`}>{title}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""} ${danger ? "text-red-300" : "text-[#8FA396]"}`} />
      </button>
      {open && <div className="mt-3">{children}</div>}
    </section>
  );
}

const RM_STATUS = [["evaluating", "In valutazione"], ["planned", "In programma"], ["in_development", "In sviluppo"], ["available", "Disponibile"]];
const RM_STATUS_LABEL = Object.fromEntries(RM_STATUS);
const RM_STATUS_CLS = { evaluating: "bg-[#3a3320] text-[#E7D9A6]", planned: "bg-[#1f3a2a] text-[#9fe3bd]", in_development: "bg-[#1e3350] text-[#a9cbff]", available: "bg-[#2a2f22] text-[#cfe6a3]" };

function RoadmapAdminSection() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => { api.get("/admin/roadmap").then((r) => setItems(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const move = async (idx, dir) => {
    const arr = [...items]; const j = idx + dir; if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]]; setItems(arr);
    try { await api.put("/admin/roadmap-order", { order: arr.map((i) => i.id) }); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); load(); }
  };
  const act = async (id, action) => {
    try { await api.post(`/admin/roadmap/${id}/${action}`); toast.success(action === "archive" ? "Elemento archiviato" : "Elemento ripristinato"); load(); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };
  const openFeedback = (r) => { window.dispatchEvent(new CustomEvent("equiro-fb-roadmap-filter", { detail: { id: r.id } })); document.getElementById("admin-feedback-section")?.scrollIntoView({ behavior: "smooth" }); };
  return (
    <section data-testid="admin-roadmap-section" id="admin-roadmap-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <h2 className="font-display text-lg font-semibold flex items-center gap-2"><Rocket className="h-5 w-5 text-[#C9A227]" /> In arrivo (Roadmap) <span className="text-[#8FA396]">({items.length})</span></h2>
        <Button size="sm" data-testid="roadmap-new" onClick={() => setEdit({})} className="bg-[#C9A227] hover:bg-[#b8941f] text-[#1A2023]"><Plus className="h-4 w-4 mr-1" /> Nuovo</Button>
      </div>
      <div className="space-y-2">
        {items.length === 0 && <p className="text-sm text-[#8FA396]" data-testid="roadmap-empty">Nessun elemento roadmap.</p>}
        {items.map((r, idx) => (
          <div key={r.id} data-testid={`roadmap-row-${r.id}`} className={`rounded-lg border border-[#333B3C] bg-[#232A2C] p-3 ${r.archived ? "opacity-60" : ""}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-[#EAE4D3]">{r.title}</span>
                  <span className={`text-[10px] font-mono-label rounded-full px-2 py-0.5 ${RM_STATUS_CLS[r.status]}`} data-testid={`roadmap-status-${r.id}`}>{RM_STATUS_LABEL[r.status]}</span>
                  {!r.visible && <span className="text-[10px] font-mono-label rounded-full px-2 py-0.5 bg-[#3a2020] text-[#f0b8b8]">Nascosto</span>}
                  {r.archived && <span className="text-[10px] font-mono-label rounded-full px-2 py-0.5 bg-[#333] text-[#ccc]">Archiviato</span>}
                </div>
                <p className="text-[11px] text-[#8FA396] mt-1 break-words">{r.short_description}</p>
                <div className="flex items-center gap-3 mt-1.5 text-[11px] text-[#8FA396] flex-wrap">
                  <span data-testid={`roadmap-interest-count-${r.id}`}>{r.interest_count} interessati</span>
                  <button type="button" data-testid={`roadmap-feedback-link-${r.id}`} onClick={() => openFeedback(r)} className="underline hover:text-[#EAE4D3]">{r.linked_feedback_count} feedback collegati</button>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button size="icon" variant="outline" data-testid={`roadmap-up-${r.id}`} disabled={idx === 0} onClick={() => move(idx, -1)} className="border-[#333B3C] text-[#EAE4D3] hover:bg-[#2E6B4B] disabled:opacity-40"><ArrowUp className="h-3.5 w-3.5" /></Button>
                <Button size="icon" variant="outline" data-testid={`roadmap-down-${r.id}`} disabled={idx === items.length - 1} onClick={() => move(idx, 1)} className="border-[#333B3C] text-[#EAE4D3] hover:bg-[#2E6B4B] disabled:opacity-40"><ArrowDown className="h-3.5 w-3.5" /></Button>
                <Button size="icon" variant="outline" data-testid={`roadmap-edit-${r.id}`} onClick={() => setEdit(r)} className="border-[#333B3C] text-[#EAE4D3] hover:bg-[#2E6B4B]"><Pencil className="h-3.5 w-3.5" /></Button>
                {r.archived
                  ? <Button size="icon" variant="outline" data-testid={`roadmap-restore-${r.id}`} onClick={() => act(r.id, "restore")} className="border-[#333B3C] text-[#EAE4D3] hover:bg-[#2E6B4B]"><RotateCcw className="h-3.5 w-3.5" /></Button>
                  : <Button size="icon" variant="outline" data-testid={`roadmap-archive-${r.id}`} onClick={() => act(r.id, "archive")} className="border-[#333B3C] text-[#EAE4D3] hover:bg-[#2E6B4B]"><ArchiveIcon className="h-3.5 w-3.5" /></Button>}
              </div>
            </div>
          </div>
        ))}
      </div>
      <RoadmapEditDialog item={edit} onClose={() => setEdit(null)} onSaved={load} />
    </section>
  );
}

function RoadmapEditDialog({ item, onClose, onSaved }) {
  const isNew = item && !item.id;
  const [title, setTitle] = useState("");
  const [shortD, setShortD] = useState("");
  const [longD, setLongD] = useState("");
  const [status, setStatus] = useState("evaluating");
  const [keywords, setKeywords] = useState("");
  const [visible, setVisible] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (item) { setTitle(item.title || ""); setShortD(item.short_description || ""); setLongD(item.long_description || ""); setStatus(item.status || "evaluating"); setKeywords((item.keywords || []).join(", ")); setVisible(item.visible !== false); }
  }, [item]);
  const save = async () => {
    if (!title.trim() || !shortD.trim()) { toast.error("Titolo e descrizione breve obbligatori."); return; }
    setBusy(true);
    const payload = { title: title.trim(), short_description: shortD.trim(), long_description: longD.trim() || null, status, visible, keywords: keywords.split(/[,\n]/).map((k) => k.trim()).filter(Boolean) };
    try {
      if (isNew) await api.post("/admin/roadmap", payload);
      else await api.put(`/admin/roadmap/${item.id}`, payload);
      toast.success(isNew ? "Elemento creato" : "Elemento aggiornato"); onSaved(); onClose();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-[#1A2023] border-[#333B3C] text-[#EAE4D3]" data-testid="roadmap-edit-dialog">
        <DialogHeader><DialogTitle className="font-display">{isNew ? "Nuovo elemento" : "Modifica elemento"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs text-[#8FA396]">Titolo</label>
            <input data-testid="roadmap-title" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full mt-1 rounded-md bg-[#232A2C] border border-[#333B3C] px-3 py-2 text-[#EAE4D3] outline-none" placeholder="Es. Alimentazione & Consumi" /></div>
          <div><label className="text-xs text-[#8FA396]">Descrizione breve</label>
            <textarea data-testid="roadmap-short" value={shortD} onChange={(e) => setShortD(e.target.value)} rows={2} className="w-full mt-1 rounded-md bg-[#232A2C] border border-[#333B3C] px-3 py-2 text-[#EAE4D3] outline-none" /></div>
          <div><label className="text-xs text-[#8FA396]">Descrizione estesa (facoltativa)</label>
            <textarea data-testid="roadmap-long" value={longD} onChange={(e) => setLongD(e.target.value)} rows={3} className="w-full mt-1 rounded-md bg-[#232A2C] border border-[#333B3C] px-3 py-2 text-[#EAE4D3] outline-none" /></div>
          <div className="flex gap-3 flex-wrap">
            <div className="flex-1 min-w-[160px]"><label className="text-xs text-[#8FA396]">Stato</label>
              <select data-testid="roadmap-status-select" value={status} onChange={(e) => setStatus(e.target.value)} className="w-full mt-1 rounded-md bg-[#232A2C] border border-[#333B3C] px-2 py-2 text-[#EAE4D3]">
                {RM_STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></div>
            <label className="flex items-center gap-2 text-sm mt-6" data-testid="roadmap-visible-wrap">
              <input type="checkbox" data-testid="roadmap-visible" checked={visible} onChange={(e) => setVisible(e.target.checked)} /> Visibile agli utenti
            </label>
          </div>
          <div><label className="text-xs text-[#8FA396]">Parole chiave (separate da virgola) — per il suggerimento nel Feedback</label>
            <textarea data-testid="roadmap-keywords" value={keywords} onChange={(e) => setKeywords(e.target.value)} rows={2} className="w-full mt-1 rounded-md bg-[#232A2C] border border-[#333B3C] px-3 py-2 text-[#EAE4D3] outline-none" placeholder="alimentazione, razione, mangime, fieno" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="roadmap-cancel" className="border-[#333B3C] text-[#EAE4D3] hover:bg-[#2E6B4B]">Annulla</Button>
          <Button onClick={save} disabled={busy} data-testid="roadmap-save" className="bg-[#C9A227] hover:bg-[#b8941f] text-[#1A2023]">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const RN_TYPE = [["feature", "Novità"], ["improvement", "Miglioramento"], ["important_fix", "Correzione importante"]];
const RN_TYPE_LABEL = Object.fromEntries(RN_TYPE);
const RN_STATUS_LABEL = { draft: "Bozza", published: "Pubblicata", archived: "Archiviata" };
const RN_STATUS_CLS = { draft: "bg-[#2E6B4B] text-[#8FA396]", published: "bg-[#1B3B2B] text-[#C9A227]", archived: "bg-[#2E6B4B] text-[#6F8377]" };

function ReleaseNotesAdminSection() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null); // note object or {} for new

  const load = useCallback(() => {
    api.get("/admin/release-notes").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    try { await api.post(`/admin/release-notes/${id}/${action}`); toast.success(action === "publish" ? "Novità pubblicata" : "Novità archiviata"); load(); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };

  return (
    <section data-testid="admin-release-notes-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <h2 className="font-display text-lg font-semibold flex items-center gap-2"><Megaphone className="h-5 w-5 text-[#C9A227]" /> Novità / Release Notes <span className="text-[#8FA396]">({items.length})</span></h2>
        <Button size="sm" data-testid="release-note-new" onClick={() => setEdit({})} className="bg-[#C9A227] hover:bg-[#b8941f] text-[#1A2023]"><Plus className="h-4 w-4 mr-1" /> Nuova</Button>
      </div>
      <div className="space-y-2">
        {items.length === 0 && <p className="text-sm text-[#8FA396]" data-testid="release-notes-empty">Nessuna release note.</p>}
        {items.map((n) => (
          <div key={n.id} data-testid={`release-note-${n.id}`} className="rounded-lg border border-[#333B3C] bg-[#232A2C] p-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] font-mono-label rounded-full px-2 py-0.5 ${RN_STATUS_CLS[n.status]}`} data-testid={`release-note-status-${n.id}`}>{RN_STATUS_LABEL[n.status]}</span>
                <span className="text-[10px] text-[#8FA396]">{RN_TYPE_LABEL[n.type]}</span>
                <p className="text-sm font-medium truncate">{n.title}</p>
              </div>
              <p className="text-[11px] text-[#8FA396] truncate mt-0.5">{n.summary}</p>
            </div>
            <div className="flex gap-1.5 shrink-0">
              <Button size="sm" variant="outline" data-testid={`release-note-edit-${n.id}`} onClick={() => setEdit(n)} className="border-[#333B3C] text-[#EAE4D3] hover:bg-[#2E6B4B]"><Pencil className="h-3.5 w-3.5" /></Button>
              {n.status !== "published" && <Button size="sm" data-testid={`release-note-publish-${n.id}`} onClick={() => act(n.id, "publish")} className="bg-[#1B3B2B] hover:bg-[#142E1F] text-[#EAE4D3]"><Send className="h-3.5 w-3.5 mr-1" /> Pubblica</Button>}
              {n.status !== "archived" && <Button size="sm" variant="outline" data-testid={`release-note-archive-${n.id}`} onClick={() => act(n.id, "archive")} className="border-[#333B3C] text-[#EAE4D3] hover:bg-[#2E6B4B]"><ArchiveIcon className="h-3.5 w-3.5" /></Button>}
            </div>
          </div>
        ))}
      </div>
      <ReleaseNoteEditDialog note={edit} onClose={() => setEdit(null)} onSaved={load} />
    </section>
  );
}

function ReleaseNoteEditDialog({ note, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [bullets, setBullets] = useState("");
  const [type, setType] = useState("feature");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (note) { setTitle(note.title || ""); setSummary(note.summary || ""); setBullets((note.bullets || []).join("\n")); setType(note.type || "feature"); }
  }, [note]);
  if (!note) return null;
  const isNew = !note.id;
  const save = async () => {
    if (!title.trim() || !summary.trim()) { toast.error("Titolo e riepilogo sono obbligatori."); return; }
    setBusy(true);
    const payload = { title: title.trim(), summary: summary.trim(), type, bullets: bullets.split("\n").map((b) => b.trim()).filter(Boolean) };
    try {
      if (isNew) await api.post("/admin/release-notes", payload);
      else await api.put(`/admin/release-notes/${note.id}`, payload);
      toast.success(isNew ? "Bozza creata" : "Novità aggiornata"); onSaved(); onClose();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!note} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-[#1A2023] border-[#333B3C] text-[#EAE4D3]" data-testid="release-note-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{isNew ? "Nuova novità" : "Modifica novità"}</DialogTitle>
          <DialogDescription className="text-xs text-[#8FA396]">Scrivi per un gestore di scuderia, senza linguaggio tecnico. La novità nasce sempre come bozza.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <p className="font-mono-label text-[10px] text-[#C9A227] mb-1">Tipo</p>
            <select data-testid="release-note-type" value={type} onChange={(e) => setType(e.target.value)} className="w-full rounded-md bg-[#232A2C] border border-[#333B3C] px-2 py-2 text-[#EAE4D3]">
              {RN_TYPE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <p className="font-mono-label text-[10px] text-[#C9A227] mb-1">Titolo</p>
            <input data-testid="release-note-title" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full rounded-md bg-[#232A2C] border border-[#333B3C] px-3 py-2 text-[#EAE4D3] outline-none" placeholder="Es. Ora puoi importare i cavalli dal tuo vecchio gestionale" />
          </div>
          <div>
            <p className="font-mono-label text-[10px] text-[#C9A227] mb-1">Riepilogo</p>
            <textarea data-testid="release-note-summary" value={summary} onChange={(e) => setSummary(e.target.value)} rows={3} className="w-full rounded-md bg-[#232A2C] border border-[#333B3C] px-3 py-2 text-[#EAE4D3] outline-none" placeholder="Una o due frasi semplici." />
          </div>
          <div>
            <p className="font-mono-label text-[10px] text-[#C9A227] mb-1">Dettagli (uno per riga, facoltativo)</p>
            <textarea data-testid="release-note-bullets" value={bullets} onChange={(e) => setBullets(e.target.value)} rows={4} className="w-full rounded-md bg-[#232A2C] border border-[#333B3C] px-3 py-2 text-[#EAE4D3] outline-none" placeholder={"Prima riga\nSeconda riga"} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#333B3C] text-[#EAE4D3] hover:bg-[#2E6B4B]">Annulla</Button>
          <Button onClick={save} disabled={busy} data-testid="release-note-save" className="bg-[#C9A227] hover:bg-[#b8941f] text-[#1A2023]">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salva bozza
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
